import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  AutoComplete,
  Button,
  Checkbox,
  Collapse,
  DatePicker,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Dropdown,
  Space,
  Spin,
  Steps,
  Table,
  Tag,
  Timeline,
} from "antd";
import {
  ArrowRightOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  ExportOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import * as dayjs from "dayjs";
import { useFirestore } from "react-redux-firebase";
import { useTranslation } from "react-i18next";
import {
  FIRESTORE_EXACT_HISTORY_TABLE,
  FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE,
  FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE,
  FIRESTORE_PURCHASE_ORDERS_TABLE,
} from "../../../../../constants/FirestoreConstant";
import { addLogEntry } from "../../../../../utils/logging";
import { formatPriceByCurrency } from "../../../../../functions/formatters";
import {
  collectContainerNumbers,
  containerNumberForStorage,
  filterContainerNumbers,
  findContainerSiblings,
  normalizeContainerNumber,
} from "../../../../../functions/containerNumber";
import urls from "../../../../../urls";
import { useSelector } from "react-redux";
import {
  usePurchaseOrderInvoices,
  usePurchaseOrderPayments,
  usePurchaseOrderShipment,
  usePurchaseOrderShipments,
  usePurchaseOrders,
} from "../../../../../data/hooks";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { getShippedQuantityForOrder } from "../../../../../utils/stockCalculations";
import {
  DetailLayout,
  KeyValueList,
  Num,
  PageHeader,
  RecordLink,
  SectionCard,
  StickyActionBar,
} from "../../../../../components/shared-components/purchase";

const { Panel } = Collapse;

const statusColors = {
  draft: "default",
  shipped: "blue",
  in_transit: "orange",
  received: "green",
  completed: "cyan",
  cancelled: "red",
};

const statusLabels = {
  draft: "Concept",
  shipped: "Verzonden",
  received: "Ontvangen",
  completed: "Afgerond",
  cancelled: "Geannuleerd",
};

const invoiceStatusColors = {
  draft: "default",
  submitted: "processing",
  approved: "blue",
  partially_paid: "orange",
  paid: "green",
  disputed: "red",
};

const invoiceStatusLabels = {
  draft: "Concept",
  submitted: "Ingediend",
  approved: "Goedgekeurd",
  partially_paid: "Deels betaald",
  paid: "Betaald",
  disputed: "Betwist",
};

const PurchaseOrderShipmentDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const firestore = useFirestore();
  const [shipment, setShipment] = useState(null);
  const [shipmentInvoiceId, setShipmentInvoiceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Eén stapindex voor de hele ontvangst-flow (null | 0 | 1 | 2) in plaats van
  // drie losse modal-booleans die elkaar opstartten.
  const [receiptFlowStep, setReceiptFlowStep] = useState(null);
  // Expliciete bevestiging in stap 2 dat de herwaardering in Exact is gedaan.
  const [revaluationConfirmed, setRevaluationConfirmed] = useState(false);
  const [receivedQuantities, setReceivedQuantities] = useState({});
  const [receiptDate, setReceiptDate] = useState(dayjs());
  const [processingReceipt, setProcessingReceipt] = useState(false);
  const [loadingCostPrices, setLoadingCostPrices] = useState(false);
  const [sendingAccountantMail, setSendingAccountantMail] = useState(false);
  const [editingShipment, setEditingShipment] = useState(false);
  // Welke inkooporder-groepen in de productentabel openstaan.
  const [expandedGroupKeys, setExpandedGroupKeys] = useState([]);
  const [editedShipmentItems, setEditedShipmentItems] = useState([]);
  const [showAddShipmentItemModal, setShowAddShipmentItemModal] = useState(false);
  // A shipment line may only be created from an existing purchase order line (BMX-18),
  // so the modal selects order lines — not free products from the supplier catalog.
  // Multiple lines can be picked at once; each keeps its own quantity/price in lineInputs.
  const [selectedOrderLineKeys, setSelectedOrderLineKeys] = useState([]);
  const [lineInputs, setLineInputs] = useState({});
  const [addItemSearch, setAddItemSearch] = useState("");
  const [showRetryExactModal, setShowRetryExactModal] = useState(false);
  const [retryingExactSync, setRetryingExactSync] = useState(false);
  const [goodsReceiptIsRetry, setGoodsReceiptIsRetry] = useState(false);
  const [costPriceLines, setCostPriceLines] = useState([]);
  const [costPriceExchangeRate, setCostPriceExchangeRate] = useState(1);
  const [updatingCostPrices, setUpdatingCostPrices] = useState(false);
  const [costPricesAlreadyUpdated, setCostPricesAlreadyUpdated] = useState(false);
  const [costPriceRetryMode, setCostPriceRetryMode] = useState(false);
  const [shipmentCurrency, setShipmentCurrency] = useState("USD");
  const [costPriceHistory, setCostPriceHistory] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [editingContainerNumber, setEditingContainerNumber] = useState(false);
  const [containerNumberValue, setContainerNumberValue] = useState("");
  const [savingContainerNumber, setSavingContainerNumber] = useState(false);
  const [editingContainerPrice, setEditingContainerPrice] = useState(false);
  const [containerPriceValue, setContainerPriceValue] = useState(null);
  const [savingContainerPrice, setSavingContainerPrice] = useState(false);
  const [editingTrackingCode, setEditingTrackingCode] = useState(false);
  const [trackingCodeValue, setTrackingCodeValue] = useState("");
  const [savingTrackingCode, setSavingTrackingCode] = useState(false);
  // Alle verzendingen, een keer opgehaald. Voedt zowel de autocomplete van het
  // containernummer als het koppelen van verzendingen binnen dezelfde container
  // (BMX-21) - beide hebben de containernummers van alle leveranciers nodig, niet
  // alleen die van de leverancier van deze verzending.
  const [allShipments, setAllShipments] = useState([]);
  // Spiegelt containerNumberValue, zodat opslaan via Enter altijd de laatste waarde
  // pakt - ook wanneer die net uit de autocomplete is gekozen en de state-update nog
  // niet verwerkt is.
  const containerNumberRef = React.useRef("");
  // Of de suggestielijst openstaat. Expliciet gestuurd, want hij mag alleen open
  // als er ook echt iets te kiezen valt - zie de AutoComplete verderop.
  const [containerListOpen, setContainerListOpen] = useState(false);
  const [showPreOrderModal, setShowPreOrderModal] = useState(false);
  const [savingRevaluationConfirm, setSavingRevaluationConfirm] = useState(false);
  const [preOrderDate, setPreOrderDate] = useState(null);
  const [preOrderShipmentIds, setPreOrderShipmentIds] = useState([]);
  const [savingPreOrder, setSavingPreOrder] = useState(false);
  const email = useSelector((state) => state.firebaseReducer.auth.email);

  // History subcollection via dedicated hook
  const { shipmentHistory, historyLoading } = usePurchaseOrderShipment(id);

  // Use hooks for invoices
  const { invoices } = usePurchaseOrderInvoices({ shipmentId: id });
  const invoice = invoices.length > 0 ? invoices[0] : null;
  const { purchaseOrders } = usePurchaseOrders();
  const { payments } = usePurchaseOrderPayments({ invoiceId: invoice?.id });

  // Bij één of twee inkooporders staan de regels meteen open — dan is de
  // groepering alleen een kopje en wil je de regels gewoon zien. Vanaf drie
  // orders (of een zending met duizenden stuks) blijft alles dicht en klap je
  // open wat je nodig hebt.
  useEffect(() => {
    if (!shipment) return;
    const orderKeys = [
      ...new Set((shipment.items || []).map((item) => item.purchaseOrderId || "__unlinked__")),
    ];
    setExpandedGroupKeys(orderKeys.length <= 2 ? orderKeys : []);
  }, [shipment?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Proforma invoices linked to source orders of this shipment
  const sourceOrderIds = React.useMemo(
    () => (shipment?.sourceOrders || []).map((so) => so.purchaseOrderId).filter(Boolean),
    [shipment],
  );
  const { invoices: allSupplierInvoices } = usePurchaseOrderInvoices({ supplierId: shipment?.supplierId });
  const proformaInvoices = React.useMemo(() => {
    return allSupplierInvoices.filter((inv) => invoice?.linkedProformaInvoiceIds?.includes(inv.id));
  }, [allSupplierInvoices, sourceOrderIds, invoice]);
  const proformaInvoiceIds = React.useMemo(() => proformaInvoices.map((inv) => inv.id), [proformaInvoices]);
  const { payments: proformaPayments } = usePurchaseOrderPayments({ invoiceIds: proformaInvoiceIds });
  const proformaPaymentsByInvoice = React.useMemo(() => {
    return proformaInvoices.map((inv) => ({
      invoice: inv,
      payments: proformaPayments.filter((p) => p.invoiceId === inv.id),
    }));
  }, [proformaInvoices, proformaPayments]);

  // All payments relevant for weighted rate: proforma + final invoice
  const allInvoicePayments = React.useMemo(
    () => [
      ...proformaPaymentsByInvoice.flatMap(({ invoice: inv, payments: ps }) =>
        ps.map((p) => ({ ...p, _invoiceNumber: inv.invoiceNumber, _invoiceType: "proforma" })),
      ),
      ...(payments || []).map((p) => ({ ...p, _invoiceNumber: invoice?.invoiceNumber, _invoiceType: "final" })),
    ],
    [proformaPaymentsByInvoice, payments, invoice],
  );

  // All shipments of this supplier — needed to know how much of an order line
  // has already been shipped (by this shipment or any other one).
  const { shipments: supplierShipments } = usePurchaseOrderShipments({
    supplierId: shipment?.supplierId,
    skip: !shipment?.supplierId,
  });

  // The order lines of the purchase orders linked to this shipment, each with
  // ordered / already shipped / still open. This is the ONLY source a shipment
  // line may be created from (BMX-18) — free products from the supplier catalog
  // are deliberately no longer offered, because a line without a purchase order
  // cannot be counted against an order line.
  const availableOrderLines = React.useMemo(() => {
    if (!shipment?.sourceOrders?.length) return [];

    return (shipment.sourceOrders || []).flatMap((so) => {
      const order = purchaseOrders.find((po) => po.id === so.purchaseOrderId);
      if (!order) return [];

      return (order.products || []).map((p) => {
        const ordered = p.adjustedAmount ?? p.amount ?? 0;
        // Everything shipped for this order line in OTHER shipments...
        const shippedElsewhere = getShippedQuantityForOrder(supplierShipments, order.id, p.productId, id);
        // ...plus whatever this shipment already carries for it. Matched strictly
        // on purchaseOrderId: a legacy line without one cannot be attributed to a
        // specific order, so it is surfaced as "Niet gekoppeld" instead of guessed.
        const inThisShipment = (editingShipment ? editedShipmentItems : shipment.items || [])
          .filter((it) => it.productId === p.productId && it.purchaseOrderId === order.id)
          .reduce((sum, it) => sum + (it.quantity || 0), 0);

        const cancelledAmount = p.cancelledAmount || 0;
        return {
          key: `${order.id}-${p.productId}`,
          purchaseOrderId: order.id,
          purchaseOrderNumber: order.orderNumber,
          productId: p.productId,
          productName: p.productName,
          productCode: p.productCode || "",
          productColor: p.productColor || null,
          unitPrice: p.adjustedPrice ?? p.price ?? 0,
          ordered,
          shipped: shippedElsewhere,
          inThisShipment,
          cancelledAmount,
          // Cancelled (BMX-13) quantity is never available to add to a shipment,
          // same as what's already shipped elsewhere or already on this shipment.
          open: Math.max(0, ordered - shippedElsewhere - inThisShipment - cancelledAmount),
        };
      });
    });
  }, [shipment, purchaseOrders, supplierShipments, editedShipmentItems, editingShipment, id]);

  const selectedOrderLines = availableOrderLines.filter((l) => selectedOrderLineKeys.includes(l.key));

  // Lines on this shipment that predate BMX-18 and carry no purchase order link.
  const unlinkedItemCount = (shipment?.items || []).filter((it) => !it.purchaseOrderId).length;

  // Load legacy payments from purchase orders using hook data
  const legacyPayments = React.useMemo(() => {
    if (!shipment?.sourceOrders || !purchaseOrders?.length) return [];

    const allLegacyPayments = [];
    const orderIds = shipment.sourceOrders.map((so) => so.purchaseOrderId).filter(Boolean);

    for (const orderId of orderIds) {
      const order = purchaseOrders.find((po) => po.id === orderId);
      if (order && order.payments && Array.isArray(order.payments)) {
        for (const payment of order.payments) {
          allLegacyPayments.push({
            ...payment,
            sourceOrderNumber: order.orderNumber,
            sourceOrderId: orderId,
            isLegacy: true,
          });
        }
      }
    }

    return allLegacyPayments;
  }, [shipment, purchaseOrders]);

  // Combine legacy payments with invoice payments (legacy first)
  const allPayments = React.useMemo(
    () => [...legacyPayments.map((p) => ({ ...p, id: `legacy-${p.sourceOrderId}-${p.paidAt}` })), ...(payments || [])],
    [legacyPayments, payments],
  );

  // Helper function to get quantity from invoice items by index
  const getInvoiceQuantity = (index) => {
    if (!invoice?.lines || index === null || index === undefined) return null;
    const invoiceLine = invoice.lines[index];
    return invoiceLine ? invoiceLine.quantity : null;
  };

  const loadCostPriceHistory = async () => {
    if (!id) return;
    try {
      const snapshot = await firestore
        .collection(FIRESTORE_EXACT_HISTORY_TABLE)
        .where("sourceId", "==", id)
        .where("operation", "==", "cost_price_update")
        .where("canRollback", "==", true)
        .orderBy("performedAt", "desc")
        .limit(1)
        .get();
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        const historyData = { id: doc.id, ...doc.data() };
        // const hoursSinceUpdate = (dayjs().unix() - historyData.performedAt) / 3600;
        // if (hoursSinceUpdate < 24) {
        setCostPriceHistory(historyData);
        // }
      }
    } catch (e) {
      console.warn("Kon kostprijs history niet laden:", e);
    }
  };

  /**
   * Haalt alle verzendingen een keer op (geen live listener, geen orderBy).
   *
   * Hier stond eerder een `where("containerNumber", "==", ...)`: een exacte match op de
   * opgeslagen tekst. Die vond "MSKU 123-456" en "msku123456" niet als dezelfde container,
   * terwijl dat fysiek een container is - en juist die koppeling voedt de bulkactie
   * "Pre-order datums", die dan maar de helft van de verzendingen raakte. Sinds BMX-21 wordt
   * op de genormaliseerde waarde vergeleken, en dat kan Firestore niet server-side; vandaar
   * dat de vergelijking hier in de client gebeurt. Bijkomend voordeel: verzendingen die voor
   * BMX-21 zijn ingevoerd of die via het leveranciersportaal binnenkomen, worden alsnog
   * gekoppeld zonder dat de bestaande data aangepast hoeft te worden.
   *
   * Bewust geen `orderBy("createdAt")` zoals de hook usePurchaseOrderShipments doet: Firestore
   * laat documenten zonder dat veld dan stilzwijgend weg, en een verzending niet aan zijn
   * container koppelen is erger dan een ongesorteerde lijst.
   */
  const loadAllShipments = async () => {
    try {
      const snap = await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).get();
      setAllShipments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.warn("Kon medeverzendingen niet laden:", e);
    }
  };

  // Verzendingen in dezelfde container, vergeleken op de genormaliseerde waarde.
  const containerShipments = React.useMemo(
    () => findContainerSiblings(allShipments, shipment?.containerNumber, id),
    [allShipments, shipment?.containerNumber, id],
  );

  // Bestaande containernummers voor de autocomplete, met het aantal verzendingen erachter.
  const containerNumberOptions = React.useMemo(() => collectContainerNumbers(allShipments), [allShipments]);

  // De suggestielijst is al genormaliseerd, dus de zoekterm ook: wie "msku 12" typt,
  // krijgt gewoon MSKU1234567 te zien.
  const filteredContainerNumbers = React.useMemo(
    () => filterContainerNumbers(containerNumberOptions, containerNumberValue),
    [containerNumberOptions, containerNumberValue],
  );

  // Hoeveel ANDERE verzendingen het zojuist getypte nummer al gebruiken. Dit is wat de
  // gebruiker tijdens het typen te zien krijgt, dus nog voor hij opslaat.
  const typedContainerSiblingCount = React.useMemo(
    () => findContainerSiblings(allShipments, containerNumberValue, id).length,
    [allShipments, containerNumberValue, id],
  );

  /**
   * Enige plek waar het bewerkveld voor het containernummer gevuld wordt. Normaliseert direct,
   * zodat wat je in het veld ziet ook letterlijk is wat er opgeslagen wordt - een bestaand,
   * nog niet genormaliseerd nummer wordt dus rechtgetrokken zodra je het openklapt.
   */
  const updateContainerNumberValue = (value) => {
    const normalized = normalizeContainerNumber(value);
    containerNumberRef.current = normalized;
    setContainerNumberValue(normalized);
  };

  const loadShipment = async () => {
    try {
      const doc = await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).get();
      if (doc.exists) {
        const shipmentData = { id: doc.id, ...doc.data() };
        setShipment(shipmentData);
        setShipmentInvoiceId(shipmentData.invoiceId || null);
        updateContainerNumberValue(shipmentData.containerNumber || "");
        setContainerPriceValue(shipmentData.containerPrice || null);
        setTrackingCodeValue(shipmentData.trackingCode || "");
        await loadAllShipments();

        // Load currency from first linked purchase order
        const firstOrderId = shipmentData.sourceOrders?.[0]?.purchaseOrderId;
        if (firstOrderId) {
          try {
            const poDoc = await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(firstOrderId).get();
            if (poDoc.exists) {
              setShipmentCurrency((poDoc.data().currency || "USD").toUpperCase());
            }
          } catch (e) {
            console.warn("Kon valuta niet ophalen:", e);
          }
        }
      } else {
        message.error(t("shipment.not_found"));
      }
    } catch (error) {
      console.error("Error loading shipment:", error);
      message.error(t("shipment.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadShipment();
      loadCostPriceHistory();
    }
  }, [id]);

  const handleSaveContainerNumber = async () => {
    try {
      setSavingContainerNumber(true);
      // Normaliseren voor het opslaan (BMX-21): hoofdletters, geen spaties, geen streepjes.
      // De ref en niet de state, zodat een net gekozen suggestie niet gemist wordt.
      const trimmed = containerNumberForStorage(containerNumberRef.current);
      const oldLog = shipment.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "shipment_updated",
        user: email,
        metadata: { field: "containerNumber", newValue: trimmed },
      });
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).update({
        containerNumber: trimmed,
        log: newLog,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      message.success(t("shipment.updated_success"));
      setEditingContainerNumber(false);
      await loadShipment();
    } catch (error) {
      console.error("Error saving container number:", error);
      message.error(t("shipment.update_failed"));
    } finally {
      setSavingContainerNumber(false);
    }
  };

  const handleSaveContainerPrice = async () => {
    try {
      setSavingContainerPrice(true);
      const price =
        containerPriceValue !== null && containerPriceValue !== undefined ? Number(containerPriceValue) : null;
      const oldLog = shipment.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "shipment_updated",
        user: email,
        metadata: { field: "containerPrice", newValue: price },
      });
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).update({
        containerPrice: price,
        log: newLog,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      message.success(t("shipment.updated_success"));
      setEditingContainerPrice(false);
      await loadShipment();
    } catch (error) {
      console.error("Error saving container price:", error);
      message.error(t("shipment.update_failed"));
    } finally {
      setSavingContainerPrice(false);
    }
  };

  const handleSaveTrackingCode = async () => {
    try {
      setSavingTrackingCode(true);
      const trimmed = trackingCodeValue.trim() || null;
      const oldLog = shipment.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "shipment_updated",
        user: email,
        metadata: { field: "trackingCode", newValue: trimmed },
      });
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).update({
        trackingCode: trimmed,
        log: newLog,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      message.success(t("shipment.updated_success"));
      setEditingTrackingCode(false);
      await loadShipment();
    } catch (error) {
      console.error("Error saving tracking code:", error);
      message.error(t("shipment.update_failed"));
    } finally {
      setSavingTrackingCode(false);
    }
  };

  const handleOpenPreOrderModal = () => {
    setPreOrderDate(null);
    setPreOrderShipmentIds([id, ...containerShipments.map((s) => s.id)]);
    setShowPreOrderModal(true);
  };

  const handleSavePreOrder = async () => {
    if (!preOrderDate) {
      message.error("Selecteer een aankomstdatum");
      return;
    }
    const dateStr = preOrderDate.format("DD-MM-YYYY");
    try {
      setSavingPreOrder(true);

      const allShipmentsToUpdate = [shipment, ...containerShipments].filter((s) => preOrderShipmentIds.includes(s.id));

      let totalUpdatedProducts = 0;

      for (const sh of allShipmentsToUpdate) {
        const productIds = (sh.items || []).map((item) => item.productId).filter(Boolean);
        const uniqueProductIds = [...new Set(productIds)];

        for (const productId of uniqueProductIds) {
          await firestore
            .collection("products")
            .doc(productId)
            .update({
              preOrder: { active: true, availableAt: dateStr },
              updatedAt: dayjs().unix(),
              updatedBy: email,
            });
          totalUpdatedProducts++;
        }

        await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(sh.id).update({
          preOrderDatesUpdated: true,
          preOrderDatesUpdatedAt: dayjs().unix(),
          preOrderAvailableAt: dateStr,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        });
      }

      message.success(`Pre-order datum ingesteld op ${dateStr} voor ${totalUpdatedProducts} producten`);
      setShowPreOrderModal(false);
      await loadShipment();
    } catch (error) {
      console.error("Error saving pre-order dates:", error);
      message.error("Fout bij instellen pre-order datums");
    } finally {
      setSavingPreOrder(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      setSaving(true);
      const oldLog = shipment.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "status_changed",
        user: email,
        oldValue: shipment.status,
        newValue: newStatus,
      });

      const updateData = {
        status: newStatus,
        log: newLog,
      };

      if (newStatus === "received") {
        updateData.receivedAt = dayjs().unix();
      }

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).update(updateData);
      message.success(t("shipment.updated_success"));
      await loadShipment();
    } catch (error) {
      console.error("Error updating shipment status:", error);
      message.error(t("shipment.update_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateInvoiceStatus = async (newStatus) => {
    if (!invoice) return;
    try {
      setSaving(true);
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      message.success("Factuurstatus bijgewerkt");
      await loadShipment();
    } catch (error) {
      console.error("Error updating invoice status:", error);
      message.error("Fout bij bijwerken factuurstatus");
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditingShipment = () => {
    setEditedShipmentItems(JSON.parse(JSON.stringify(shipment.items || [])));
    setEditingShipment(true);
  };

  const handleCancelEditingShipment = () => {
    setEditingShipment(false);
    setEditedShipmentItems([]);
  };

  const handleShipmentItemChange = (index, field, value) => {
    const updated = [...editedShipmentItems];
    updated[index] = { ...updated[index], [field]: value };
    setEditedShipmentItems(updated);
  };

  const handleRemoveShipmentItem = (index) => {
    setEditedShipmentItems(editedShipmentItems.filter((_, i) => i !== index));
    message.success("Product verwijderd uit zending");
  };

  const handleOpenAddShipmentItemModal = () => {
    setSelectedOrderLineKeys([]);
    setLineInputs({});
    setAddItemSearch("");
    setShowAddShipmentItemModal(true);
  };

  const handleCloseAddShipmentItemModal = () => {
    setShowAddShipmentItemModal(false);
    setSelectedOrderLineKeys([]);
    setLineInputs({});
    setAddItemSearch("");
  };

  // Selecting a line prefills its quantity with what's still open on that line and
  // the price agreed on the order itself (not the product's catalog price). Values
  // already edited by the user are kept when the selection changes.
  const handleSelectionChange = (keys) => {
    setSelectedOrderLineKeys(keys);
    setLineInputs((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        if (next[key]) return;
        const line = availableOrderLines.find((l) => l.key === key);
        if (!line) return;
        next[key] = {
          quantity: line.open > 0 ? line.open : 1,
          unitPrice: line.unitPrice || 0,
        };
      });
      return next;
    });
  };

  const handleToggleOrderLine = (lineKey) => {
    handleSelectionChange(
      selectedOrderLineKeys.includes(lineKey)
        ? selectedOrderLineKeys.filter((k) => k !== lineKey)
        : [...selectedOrderLineKeys, lineKey],
    );
  };

  const handleLineInputChange = (lineKey, field, value) => {
    setLineInputs((prev) => ({ ...prev, [lineKey]: { ...prev[lineKey], [field]: value } }));
  };

  const handleAddShipmentItems = () => {
    if (selectedOrderLines.length === 0) {
      message.error("Selecteer minimaal één orderregel");
      return;
    }

    const invalid = selectedOrderLines.find((line) => {
      const input = lineInputs[line.key] || {};
      return !input.quantity || input.quantity <= 0 || !input.unitPrice || input.unitPrice <= 0;
    });
    if (invalid) {
      message.error(`Vul een geldige hoeveelheid en prijs in voor ${invalid.productName}`);
      return;
    }

    const newItems = selectedOrderLines.map((line) => ({
      purchaseOrderId: line.purchaseOrderId,
      purchaseOrderNumber: line.purchaseOrderNumber,
      productId: line.productId,
      productCode: line.productCode || "",
      productName: line.productName,
      productColor: line.productColor || null,
      quantity: lineInputs[line.key].quantity,
      unitPrice: lineInputs[line.key].unitPrice,
    }));

    setEditedShipmentItems([...editedShipmentItems, ...newItems]);
    handleCloseAddShipmentItemModal();
    message.success(
      newItems.length === 1
        ? `Product toegevoegd vanuit order ${newItems[0].purchaseOrderNumber}`
        : `${newItems.length} producten toegevoegd aan de verzending`,
    );
  };

  const handleSaveShipmentEdits = async () => {
    try {
      setSaving(true);

      // Recalculate totals
      const totalProducts = editedShipmentItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const totalPrice = editedShipmentItems.reduce(
        (sum, item) => sum + (item.quantity || 0) * (item.unitPrice || 0),
        0,
      );

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).update({
        items: editedShipmentItems,
        totalProducts,
        totalPrice,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      message.success("Verzending bijgewerkt");
      setEditingShipment(false);
      await loadShipment();
    } catch (error) {
      console.error("Error saving shipment edits:", error);
      message.error("Fout bij opslaan wijzigingen");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenGoodsReceiptModal = async () => {
    const initialQuantities = {};
    (shipment.items || []).forEach((item, index) => {
      initialQuantities[index] = item.quantity;
    });
    setReceivedQuantities(initialQuantities);
    setReceiptDate(dayjs());
    setGoodsReceiptIsRetry(false);
    setReceiptFlowStep(2);
  };

  const handleResendAccountantMail = async () => {
    try {
      setSendingAccountantMail(true);
      const idToken = await getFirebaseIDToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/exact/shipments/${id}/notify-accountant`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken },
      });

      if (response.ok) {
        const accountantMailSentAt = dayjs().unix();
        await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).update({ accountantMailSentAt });
        message.success("Mail naar boekhouder opnieuw verstuurd");
        await loadShipment(); // Refresh to show the new timestamp
      } else {
        throw new Error("Kon mail niet versturen");
      }
    } catch (error) {
      console.error("Error resending accountant mail:", error);
      message.error("Fout bij versturen mail naar boekhouder");
    } finally {
      setSendingAccountantMail(false);
    }
  };

  const handleOpenCostPriceModal = async (retryMode = false) => {
    setCostPriceRetryMode(retryMode);
    setLoadingCostPrices(true);
    const firstOrder = shipment.sourceOrders?.[0];
    let exchangeRate = 1;

    try {
      // Calculate weighted exchange rate based on invoice payments only
      let totalAmount = 0;
      let weightedRateSum = 0;

      // Use all invoice payments (proforma + final) for weighted rate
      const invoicePayments = allInvoicePayments.filter((p) => p.exchangeRate);

      for (const payment of invoicePayments) {
        const amount = Number(payment.amount) || 0;
        const rate = Number(payment.exchangeRate) || 1;

        totalAmount += amount;
        weightedRateSum += amount * rate;
      }

      // If we have invoice payments, use the weighted average; otherwise fall back to PO rate
      if (totalAmount > 0) {
        exchangeRate = weightedRateSum / totalAmount;
        console.log(`Gewogen wisselkoers berekend: ${exchangeRate} (totaal bedrag: ${totalAmount})`);
      } else if (firstOrder?.purchaseOrderId) {
        // Fallback: use exchange rate from first purchase order
        const poDoc = await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(firstOrder.purchaseOrderId).get();
        if (poDoc.exists) {
          const poData = poDoc.data();
          exchangeRate = poData.exchangeRate ? Number(poData.exchangeRate) : 1;
          console.log(`Fallback wisselkoers van PO: ${exchangeRate}`);
        }
      }

      if (!shipment.supplierId) {
        message.error("Geen leverancier gekoppeld aan deze verzending.");
        return;
      }
    } catch (e) {
      console.warn("Kon wisselkoers of leverancier niet ophalen:", e);
      message.error("Fout bij ophalen gegevens: " + e.message);
      return;
    }

    const lines = (shipment.items || []).map((item) => ({
      itemCode: item.productCode,
      productName: item.productName,
      unitPriceOriginal: item.unitPrice || 0,
      exchangeRate,
      newCostPriceEur: Math.round((item.unitPrice || 0) * exchangeRate * 100) / 100,
    }));

    // Check if all cost prices have already been updated in Exact. If this check itself
    // fails, we genuinely don't know - default to "not updated" (allUpdated = false) so the
    // user isn't told to skip a step that may not have happened. The previous "calculation
    // fallback" here compared newCostPriceEur against a value computed with the exact same
    // formula, so it was always true regardless of Exact's actual state - it looked like a
    // safety net but never actually verified anything.
    let allUpdated = false;
    let costPriceCheckFailed = false;

    try {
      const idToken = await getFirebaseIDToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/exact/items/current-cost-prices`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken },
        body: JSON.stringify({
          itemCodes: lines.map((l) => l.itemCode),
        }),
      });

      if (response.ok) {
        const currentCostPrices = await response.json();
        allUpdated = lines.every((line) => {
          const expectedCostPrice = Math.round((line.unitPriceOriginal || 0) * exchangeRate * 100) / 100;
          const currentCostPrice =
            currentCostPrices.find((cp) => cp.itemCode === line.itemCode)?.costPriceStandard || 0;
          // De huidige kostprijs werd alleen gebruikt voor deze ja/nee-check en daarna
          // weggegooid. Het ontwerp toont hem als kolom "Huidig EUR" naast "Nieuw EUR",
          // zodat je ziet wat er verandert in plaats van alleen wat het wordt.
          line.currentCostPriceEur = currentCostPrice;
          return Math.abs(currentCostPrice - expectedCostPrice) < 0.01; // Allow small rounding differences
        });
      } else {
        const err = await response.text();
        console.error("Kon huidige kostprijzen niet ophalen uit Exact:", response.status, err);
        costPriceCheckFailed = true;
      }
    } catch (error) {
      console.error("Fout bij ophalen huidige kostprijzen uit Exact:", error);
      costPriceCheckFailed = true;
    } finally {
      setLoadingCostPrices(false);
    }

    setCostPriceExchangeRate(exchangeRate);
    setCostPriceLines(lines);
    setReceiptFlowStep(0);

    setCostPricesAlreadyUpdated(allUpdated);

    if (costPriceCheckFailed) {
      message.error(
        "Kon niet verifiëren of de kostprijzen al bijgewerkt zijn in Exact (API-fout). Ga ervan uit dat dit nog moet gebeuren.",
      );
    } else if (allUpdated) {
      message.info("Alle kostprijzen zijn al bijgewerkt met de huidige wisselkoers.");
    }
  };

  const handleCostPriceLineChange = (index, field, value) => {
    const updated = [...costPriceLines];
    updated[index] = { ...updated[index], [field]: value };
    if (field === "exchangeRate") {
      updated[index].newCostPriceEur = Math.round((updated[index].unitPriceOriginal || 0) * value * 100) / 100;
    }
    setCostPriceLines(updated);
    setCostPricesAlreadyUpdated(false);
  };

  const handleExchangeRateChange = (value) => {
    const rate = value || 1;
    setCostPriceExchangeRate(rate);
    setCostPricesAlreadyUpdated(false);
    setCostPriceLines(
      costPriceLines.map((line) => ({
        ...line,
        exchangeRate: rate,
        newCostPriceEur: Math.round((line.unitPriceOriginal || 0) * rate * 100) / 100,
      })),
    );
  };

  const handleConfirmCostPriceUpdate = async () => {
    try {
      setUpdatingCostPrices(true);
      const idToken = await getFirebaseIDToken();

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/exact/purchase-orders/update-cost-prices`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken },
        body: JSON.stringify({
          exchangeRate: costPriceExchangeRate,
          lines: costPriceLines.map((l) => ({
            itemCode: l.itemCode,
            unitPriceOriginal: l.unitPriceOriginal,
            newCostPriceEur: l.newCostPriceEur,
          })),
        }),
      });
      if (!response.ok) {
        const err = await response.text();
        throw new Error(err || "API fout bij kostprijsupdate");
      }
      const result = await response.json();

      // Save history record for rollback capability
      const historyRecord = {
        entityType: "item",
        entityId: "bulk_cost_price_update",
        operation: "cost_price_update",
        oldValues: {
          items: result.results.map((r) => ({
            itemCode: r.itemCode,
            itemName: r.itemName,
            costPriceStandard: r.oldCostPriceEur,
          })),
        },
        newValues: {
          exchangeRate: costPriceExchangeRate,
          items: result.results.map((r) => ({
            itemCode: r.itemCode,
            itemName: r.itemName,
            costPriceStandard: r.newCostPriceEur,
          })),
        },
        sourceType: "purchaseOrderShipment",
        sourceId: shipment.id,
        performedBy: email,
        performedAt: dayjs().unix(),
        canRollback: true,
        metadata: {
          shipmentNumber: shipment.shipmentNumber,
          updatedCount: result.updatedCount,
          skippedCount: result.skippedCount,
        },
      };

      await firestore.collection(FIRESTORE_EXACT_HISTORY_TABLE).add(historyRecord);

      message.success(`Kostprijzen bijgewerkt: ${result.updatedCount} artikel(en) gesynchroniseerd naar Exact.`);
      if (!costPriceRetryMode) {
        setRevaluationConfirmed(false);
        setReceiptFlowStep(1);
      } else {
        setReceiptFlowStep(null);
        await loadCostPriceHistory();
      }
    } catch (error) {
      console.error("Fout bij kostprijsupdate:", error);
      message.error(`Fout bij kostprijsupdate: ${error.message}`);
    } finally {
      setUpdatingCostPrices(false);
    }
  };

  // Alleen nog gekoppeld aan de knop "Overslaan (alleen tellen)". Het kruisje en
  // Esc gaan naar handleCloseReceiptFlow, want die schoven hier ongemerkt door
  // naar stap 2 — waar je een herwaardering kon bevestigen die nooit is gedaan.
  const handleSkipCostPriceUpdate = () => {
    if (costPriceRetryMode) {
      setReceiptFlowStep(null);
      return;
    }
    setRevaluationConfirmed(false);
    setReceiptFlowStep(1);
  };

  const handleCloseReceiptFlow = () => {
    if (receiptFlowStep === 0 || receiptFlowStep === null) {
      // Stap 1 heeft nog niets weggeschreven.
      setReceiptFlowStep(null);
      return;
    }
    Modal.confirm({
      title: "Ontvangst-flow afsluiten?",
      content:
        receiptFlowStep === 1
          ? "De kostprijzen zijn al bijgewerkt in Exact. Die blijven staan — alleen de herwaardering en de telling zijn nog niet vastgelegd."
          : "De kostprijzen zijn bijgewerkt en de herwaardering is vastgelegd. Alleen de voorraadtelling is nog niet gemaakt.",
      okText: "Sluiten",
      cancelText: "Terug",
      onOk: () => setReceiptFlowStep(null),
    });
  };

  const handleConfirmRevaluation = async () => {
    try {
      setSavingRevaluationConfirm(true);
      const now = dayjs().unix();
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).update({
        exactRevaluationConfirmedAt: now,
        exactRevaluationConfirmedBy: email,
        updatedAt: now,
        updatedBy: email,
      });
      // Initialize received quantities with shipped quantities
      const initialQuantities = {};
      (shipment.items || []).forEach((item, index) => {
        initialQuantities[index] = item.quantity;
      });
      setReceivedQuantities(initialQuantities);
      setReceiptDate(dayjs());
      setGoodsReceiptIsRetry(false);
      setReceiptFlowStep(2);
      await loadShipment();
    } catch (error) {
      console.error("Fout bij opslaan herwaardering-bevestiging:", error);
      message.error("Fout bij opslaan bevestiging: " + error.message);
    } finally {
      setSavingRevaluationConfirm(false);
    }
  };

  const handleRollbackCostPrices = async () => {
    if (!costPriceHistory) return;
    try {
      setRollingBack(true);
      const idToken = await getFirebaseIDToken();

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/exact/purchase-orders/rollback-cost-prices`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken },
        body: JSON.stringify({
          exchangeRate: 1,
          lines: costPriceHistory.oldValues.items.map((item) => ({
            itemCode: item.itemCode,
            unitPriceOriginal: 0,
            newCostPriceEur: item.costPriceStandard,
          })),
        }),
      });
      if (!response.ok) {
        const err = await response.text();
        throw new Error(err || "API fout bij rollback");
      }
      const result = await response.json();

      await firestore.collection(FIRESTORE_EXACT_HISTORY_TABLE).doc(costPriceHistory.id).update({
        canRollback: false,
        rolledBackAt: dayjs().unix(),
        rolledBackBy: email,
      });

      const rollbackHistoryRecord = {
        entityType: "item",
        entityId: "bulk_cost_price_rollback",
        operation: "cost_price_rollback",
        oldValues: costPriceHistory.newValues,
        newValues: costPriceHistory.oldValues,
        sourceType: "purchaseOrderShipment",
        sourceId: shipment.id,
        performedBy: email,
        performedAt: dayjs().unix(),
        canRollback: false,
        metadata: {
          shipmentNumber: shipment.shipmentNumber,
          originalHistoryId: costPriceHistory.id,
          updatedCount: result.updatedCount,
        },
      };
      await firestore.collection(FIRESTORE_EXACT_HISTORY_TABLE).add(rollbackHistoryRecord);

      message.success(`Kostprijzen teruggedraaid: ${result.updatedCount} artikel(en) hersteld.`);
      setCostPriceHistory(null);
    } catch (error) {
      console.error("Fout bij rollback:", error);
      message.error(`Fout bij terugdraaien: ${error.message}`);
    } finally {
      setRollingBack(false);
    }
  };

  const handleProcessStockCount = async () => {
    try {
      setProcessingReceipt(true);

      const lines = (shipment.items || []).map((item, index) => ({
        itemCode: item.productCode,
        orderedAmount: item.quantity,
        receivedAmount: receivedQuantities[index] || 0,
      }));

      const idToken = await getFirebaseIDToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/exact/shipments/${id}/stock-count`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken },
        body: JSON.stringify({
          receiptDate: receiptDate.format("DD-MM-YYYY"),
          lines,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "API fout");
      }

      const result = await response.json();
      const stockCountId = result.stockCountId || null;

      const oldLog = shipment.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "stock_count_processed",
        user: email,
        receiptDate: receiptDate.unix(),
        exactStockCountId: stockCountId,
      });

      await firestore
        .collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE)
        .doc(id)
        .update({
          status: "completed",
          receivedAt: receiptDate.unix(),
          receivedQuantities: lines.map((l) => ({
            productCode: l.itemCode,
            orderedAmount: l.orderedAmount,
            receivedAmount: l.receivedAmount,
          })),
          exactStockCountId: stockCountId,
          exactStockCountSyncedAt: dayjs().unix(),
          exactStockCountStatus: "synced",
          log: newLog,
        });

      message.success("Voorraadtelling verwerkt! Voorraad bijgewerkt in Exact.");
      setReceiptFlowStep(null);
      await loadShipment();
    } catch (error) {
      console.error("Error processing stock count:", error);
      message.error(`Fout bij verwerken voorraadtelling: ${error.message}`);
    } finally {
      setProcessingReceipt(false);
    }
  };

  const handleRetryExactSync = () => {
    const initialQuantities = {};
    if (shipment.receivedQuantities?.length) {
      shipment.receivedQuantities.forEach((rq) => {
        const itemIndex = (shipment.items || []).findIndex((item) => item.productCode === rq.productCode);
        if (itemIndex !== -1) initialQuantities[itemIndex] = rq.receivedAmount;
      });
    } else {
      (shipment.items || []).forEach((item, index) => {
        initialQuantities[index] = item.quantity;
      });
    }
    setReceivedQuantities(initialQuantities);
    setReceiptDate(shipment.receivedAt ? dayjs.unix(shipment.receivedAt) : dayjs());
    setGoodsReceiptIsRetry(true);
    setShowRetryExactModal(false);
    setReceiptFlowStep(2);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!shipment) return null;

  // Regels gegroepeerd per inkooporder. `_index` blijft de positie in de platte
  // lijst, want de bewerk-handlers indexeren daarop.
  const activeItems = editingShipment ? editedShipmentItems : shipment.items || [];
  const itemGroups = (() => {
    const groups = new Map();
    activeItems.forEach((item, index) => {
      const key = item.purchaseOrderId || "__unlinked__";
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          purchaseOrderId: item.purchaseOrderId || null,
          purchaseOrderNumber: item.purchaseOrderNumber || null,
          lines: [],
        });
      }
      groups.get(key).lines.push({ ...item, _index: index, key: index });
    });
    return [...groups.values()].map((group) => ({
      ...group,
      quantity: group.lines.reduce((sum, line) => sum + (line.quantity || 0), 0),
      total: group.lines.reduce((sum, line) => sum + (line.quantity || 0) * (line.unitPrice || 0), 0),
    }));
  })();

  const shipmentTotals = {
    quantity: itemGroups.reduce((sum, group) => sum + group.quantity, 0),
    total: itemGroups.reduce((sum, group) => sum + group.total, 0),
  };

  const handleToggleAllGroups = () =>
    setExpandedGroupKeys(
      expandedGroupKeys.length === itemGroups.length ? [] : itemGroups.map((group) => group.key),
    );

  // Zeldzame vervolgacties; alleen tonen wanneer ze daadwerkelijk kunnen.
  const extraActions = [
    shipment.status === "completed" && !shipment.exactStockCountId
      ? { key: "exact-sync", label: "Synchroniseer naar Exact" }
      : null,
    shipment.status === "completed" && shipment.exactStockCountId && !shipment.accountantMailSentAt
      ? { key: "accountant-mail", label: "Mail boekhouder versturen" }
      : null,
    costPriceHistory && (email.includes("digitalkiss") || email.includes("gideloff"))
      ? { key: "cost-prices", label: "Kostprijzen opnieuw bijwerken" }
      : null,
  ].filter(Boolean);

  const milestones = [
    shipment.preOrderDatesUpdated
      ? {
          label: `Pre-order datums ingesteld op ${shipment.preOrderAvailableAt}`,
          detail: shipment.preOrderDatesUpdatedAt
            ? dayjs.unix(shipment.preOrderDatesUpdatedAt).format("DD-MM-YYYY HH:mm")
            : "",
        }
      : null,
    shipment.accountantMailSentAt
      ? {
          label: "Mail boekhouder verstuurd",
          detail: dayjs.unix(shipment.accountantMailSentAt).format("DD-MM-YYYY HH:mm"),
        }
      : null,
    shipment.exactRevaluationConfirmedAt
      ? {
          label: "Herwaardering bevestigd",
          detail: `${dayjs.unix(shipment.exactRevaluationConfirmedAt).format("DD-MM-YYYY HH:mm")}${
            shipment.exactRevaluationConfirmedBy ? ` · ${shipment.exactRevaluationConfirmedBy}` : ""
          }`,
        }
      : null,
    shipment.exactStockCountSyncedAt
      ? {
          label: "Exact voorraadtelling gemaakt",
          detail: dayjs.unix(shipment.exactStockCountSyncedAt).format("DD-MM-YYYY HH:mm"),
        }
      : null,
  ].filter(Boolean);

  // Alleen een echte http(s)-URL is te openen; een kale code niet.
  const trackingUrl = /^https?:\/\//i.test(shipment.trackingCode || "") ? shipment.trackingCode : null;

  const editSummary = `${itemGroups.length} order(s) · ${activeItems.length} regels · ${shipmentTotals.quantity} stuks · ${formatPriceByCurrency(shipmentTotals.total, shipmentCurrency)}`;

  return (
    <div className="purchase-detail-page">
      <PageHeader
        title={shipment.shipmentNumber}
        meta={
          <>
            <span>{shipment.supplierName || "-"}</span>
            <span style={{ color: "rgba(0, 0, 0, 0.15)" }}>·</span>
            <Num>{dayjs.unix(shipment.createdAt).format("DD-MM-YYYY HH:mm")}</Num>
            {shipment.createdBy && (
              <>
                <span style={{ color: "rgba(0, 0, 0, 0.15)" }}>·</span>
                <span>door {shipment.createdBy}</span>
              </>
            )}
          </>
        }
        extra={
          <>
            <Select
              value={shipment.status}
              style={{ minWidth: 180 }}
              onChange={handleUpdateStatus}
              loading={saving}
              options={Object.entries(statusLabels).map(([value, label]) => ({ value, label }))}
            />
            {shipment.status === "received" && (
              <Button type="primary" onClick={() => handleOpenCostPriceModal(false)} loading={loadingCostPrices}>
                Verwerk ontvangst
              </Button>
            )}
            {/* Uitzonderingsacties (opnieuw syncen, mail boekhouder) zaten als losse
                knoppen naast de primaire actie. Ze zijn zeldzaam en situatie-
                afhankelijk, dus ze horen onder een menu in plaats van in de weg. */}
            {extraActions.length > 0 && (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: extraActions,
                  onClick: ({ key }) => {
                    if (key === "exact-sync") handleRetryExactSync();
                    if (key === "cost-prices") handleOpenCostPriceModal(true);
                    if (key === "accountant-mail") {
                      // Verstuurt een echte mail naar de boekhouder; stond eerder
                      // achter een Popconfirm en houdt hier dezelfde bevestiging.
                      Modal.confirm({
                        title: "Mail boekhouder versturen",
                        content: "Weet je zeker dat je de mail naar de boekhouder wilt versturen?",
                        okText: "Ja, versturen",
                        cancelText: "Annuleren",
                        onOk: handleResendAccountantMail,
                      });
                    }
                  },
                }}
              >
                <Button icon={<MoreOutlined />} loading={sendingAccountantMail || loadingCostPrices} />
              </Dropdown>
            )}
          </>
        }
      />

      <DetailLayout
        aside={
          <>
            <SectionCard title="Gegevens">
              <KeyValueList
                items={[
                  {
                    label: "Status",
                    value: <Tag color={statusColors[shipment.status]}>{statusLabels[shipment.status]}</Tag>,
                  },
                  { label: t("supplier.supplier"), value: shipment.supplierName },
                  { label: t("date"), value: <Num>{dayjs.unix(shipment.createdAt).format("DD-MM-YYYY HH:mm")}</Num> },
                  shipment.etd
                    ? { label: t("etd"), value: <Num>{dayjs.unix(shipment.etd).format("DD-MM-YYYY")}</Num> }
                    : null,
                  shipment.shippedAt
                    ? {
                        label: "Verzonden op",
                        value: <Num>{dayjs.unix(shipment.shippedAt).format("DD-MM-YYYY HH:mm")}</Num>,
                      }
                    : null,
                  shipment.receivedAt
                    ? {
                        label: t("received_at"),
                        value: <Num>{dayjs.unix(shipment.receivedAt).format("DD-MM-YYYY HH:mm")}</Num>,
                      }
                    : null,
                  shipment.cubicMeters ? { label: t("cubic_meters"), value: <Num>{shipment.cubicMeters} m³</Num> } : null,
                  {
                    // Waarde, bewerk-affordance en invulknop zitten nu in aparte
                    // slots van KeyValueList; eerder propten ze samen in één cel,
                    // waardoor de niet-krimpbare knop de waarde platdrukte.
                    label: t("container_number"),
                    // Tijdens het bewerken staat het label op een eigen regel met het veld
                    // eronder: naast een label van ~119px bleef er voor een tekstveld met twee
                    // knoppen ernaast te weinig over om te kunnen zien wat je typt.
                    stacked: editingContainerNumber,
                    value: editingContainerNumber ? (
                      <div>
                        <Space.Compact style={{ width: "100%" }}>
                          <AutoComplete
                            size="small"
                            autoFocus
                            // `flex: 1` en niet `width: 100%`: Space.Compact is een flexrij, en
                            // een select van 100% breed duwt de twee knoppen eruit in plaats van
                            // de ruimte te vullen die zij overlaten. `minWidth: 0` zodat hij ook
                            // echt mag krimpen.
                            style={{ flex: 1, minWidth: 0 }}
                            value={containerNumberValue}
                            onChange={(value) => {
                              updateContainerNumberValue(value);
                              setContainerListOpen(true);
                            }}
                            onSelect={(value) => {
                              updateContainerNumberValue(value);
                              setContainerListOpen(false);
                            }}
                            onFocus={() => setContainerListOpen(true)}
                            onBlur={() => setContainerListOpen(false)}
                            // Alleen openklappen als er iets te kiezen valt. Zonder dit liet
                            // Ant Design bij een nummer dat nergens op matcht een leeg vakje
                            // onder het veld zien, wat las als "er is een suggestie, maar hij
                            // is leeg".
                            open={containerListOpen && filteredContainerNumbers.length > 0}
                            notFoundContent={null}
                            // Geen suggestie voorselecteren: Enter moet opslaan wat er getypt
                            // is, niet de bovenste suggestie kiezen.
                            defaultActiveFirstOption={false}
                            options={filteredContainerNumbers.map((c) => ({
                              value: c.value,
                              label: (
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                  <span>{c.value}</span>
                                  <span style={{ color: "rgba(0, 0, 0, 0.4)" }}>
                                    {c.count} {c.count === 1 ? "verzending" : "verzendingen"}
                                  </span>
                                </div>
                              ),
                            }))}
                            placeholder="bijv. MSCU1234567"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                setContainerListOpen(false);
                                handleSaveContainerNumber();
                              }
                            }}
                          />
                          <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            title="Opslaan"
                            loading={savingContainerNumber}
                            onClick={handleSaveContainerNumber}
                          />
                          <Button
                            size="small"
                            onClick={() => {
                              setEditingContainerNumber(false);
                              updateContainerNumberValue(shipment.containerNumber || "");
                            }}
                            icon={<CloseOutlined />}
                            title="Annuleren"
                          />
                        </Space.Compact>
                        {typedContainerSiblingCount > 0 && (
                          <div style={{ marginTop: 6, fontSize: 12, color: "#1677ff" }}>
                            Zit al in {typedContainerSiblingCount}{" "}
                            {typedContainerSiblingCount === 1 ? "andere verzending" : "andere verzendingen"}.
                          </div>
                        )}
                      </div>
                    ) : (
                      shipment.containerNumber && <Num>{shipment.containerNumber}</Num>
                    ),
                    action: editingContainerNumber ? null : (
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        title={t("edit")}
                        onClick={() => setEditingContainerNumber(true)}
                      />
                    ),
                    emptyAction: editingContainerNumber ? null : (
                      <Button size="small" onClick={() => setEditingContainerNumber(true)}>
                        Nummer invullen
                      </Button>
                    ),
                  },
                  {
                    label: t("container_price"),
                    value: editingContainerPrice ? (
                      <Space.Compact style={{ width: "100%" }}>
                        <InputNumber
                          size="small"
                          autoFocus
                          value={containerPriceValue}
                          onChange={(v) => setContainerPriceValue(v)}
                          placeholder="0.00"
                          prefix="$"
                          step={0.01}
                          precision={2}
                          style={{ width: "100%" }}
                          onPressEnter={handleSaveContainerPrice}
                        />
                        <Button
                          size="small"
                          type="primary"
                          icon={<CheckOutlined />}
                          title="Opslaan"
                          loading={savingContainerPrice}
                          onClick={handleSaveContainerPrice}
                        />
                        <Button
                          size="small"
                          onClick={() => {
                            setEditingContainerPrice(false);
                            setContainerPriceValue(shipment.containerPrice || null);
                          }}
                          icon={<CloseOutlined />}
                          title="Annuleren"
                        />
                      </Space.Compact>
                    ) : (
                      shipment.containerPrice && <Num>{formatPriceByCurrency(shipment.containerPrice, "USD")}</Num>
                    ),
                    action: editingContainerPrice ? null : (
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        title={t("edit")}
                        onClick={() => setEditingContainerPrice(true)}
                      />
                    ),
                    emptyAction: editingContainerPrice ? null : (
                      <Button size="small" onClick={() => setEditingContainerPrice(true)}>
                        Prijs invullen
                      </Button>
                    ),
                  },
                  {
                    label: t("tracking_code"),
                    // Een track & trace van een rederij is een URL van ~110 tekens.
                    // Die uitschrijven in een kolom van 154px gaf een verticale
                    // sliert van tien regels. Is het een URL, dan tonen we een
                    // korte "Openen"-link met de volledige URL als tooltip; een
                    // kale code laten we wél afbreken, want die wil je lezen.
                    value: editingTrackingCode ? (
                      <Space.Compact style={{ width: "100%" }}>
                        <Input
                          size="small"
                          autoFocus
                          value={trackingCodeValue}
                          onChange={(e) => setTrackingCodeValue(e.target.value)}
                          placeholder="bijv. https://track.example.com/123"
                          onPressEnter={handleSaveTrackingCode}
                        />
                        <Button
                          size="small"
                          type="primary"
                          icon={<CheckOutlined />}
                          title="Opslaan"
                          loading={savingTrackingCode}
                          onClick={handleSaveTrackingCode}
                        />
                        <Button
                          size="small"
                          onClick={() => {
                            setEditingTrackingCode(false);
                            setTrackingCodeValue(shipment.trackingCode || "");
                          }}
                          icon={<CloseOutlined />}
                          title="Annuleren"
                        />
                      </Space.Compact>
                    ) : trackingUrl ? (
                      <a href={trackingUrl} target="_blank" rel="noopener noreferrer" title={trackingUrl}>
                        Openen <ExportOutlined style={{ fontSize: 11 }} />
                      </a>
                    ) : (
                      shipment.trackingCode && <Num nowrap={false}>{shipment.trackingCode}</Num>
                    ),
                    wrap: !trackingUrl,
                    action: editingTrackingCode ? null : (
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        title={t("edit")}
                        onClick={() => setEditingTrackingCode(true)}
                      />
                    ),
                    emptyAction: editingTrackingCode ? null : (
                      <Button size="small" onClick={() => setEditingTrackingCode(true)}>
                        Code invullen
                      </Button>
                    ),
                  },
                  { label: t("total_products"), value: <Num>{shipment.totalProducts}</Num> },
                  {
                    label: t("total_price"),
                    value: (
                      <Num strong size={14}>
                        {formatPriceByCurrency(shipment.totalPrice || 0, shipmentCurrency)}
                      </Num>
                    ),
                  },
                ]}
              />
            </SectionCard>

            {/* Afgeronde processtappen (pre-order, mail boekhouder, herwaardering,
                Exact-telling) stonden verspreid tussen de gewone velden. Als eigen
                blokje is te zien wat al gedaan is zonder de gegevens te vertroebelen. */}
            {milestones.length > 0 && (
              <SectionCard title="Afgehandeld" meta={`${milestones.length}`}>
                <div style={{ padding: "12px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {milestones.map((m) => (
                    <div key={m.label} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                      <span style={{ color: "#52c41a", flex: "none" }}>✓</span>
                      <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 13 }}>{m.label}</span>
                        <span style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.4)" }}>{m.detail}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            <SectionCard
              title={t("order.purchase_orders")}
              meta={`${(shipment.sourceOrders || []).length} gekoppeld`}
            >
              {(shipment.sourceOrders || []).length === 0 ? (
                <div style={{ padding: "14px 20px", fontSize: 13, color: "rgba(0, 0, 0, 0.4)" }}>
                  Geen inkooporders gekoppeld.
                </div>
              ) : (
                <div>
                  {(shipment.sourceOrders || []).map((so) => {
                    const group = itemGroups.find((g) => g.purchaseOrderId === so.purchaseOrderId);
                    return (
                      <div
                        key={so.purchaseOrderId}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) auto",
                          gap: 10,
                          alignItems: "center",
                          padding: "10px 20px",
                          borderBottom: "1px solid rgba(5, 5, 5, 0.04)",
                        }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                          <RecordLink to={`${urls.purchaseOrdersList}/${so.purchaseOrderId}`}>
                            {so.purchaseOrderNumber || so.purchaseOrderId}
                          </RecordLink>
                          {group && (
                            <span style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.4)" }}>
                              {group.lines.length} regels · {group.quantity} stuks
                            </span>
                          )}
                        </div>
                        {group && <Num>{formatPriceByCurrency(group.total, shipmentCurrency)}</Num>}
                      </div>
                    );
                  })}
                </div>
              )}
            </SectionCard>

            {/* Container grouping */}
            {shipment.containerNumber && (
              <SectionCard
                title="Container"
                meta={shipment.containerNumber}
                extra={
                  <Button type="primary" size="small" icon={<CheckOutlined />} onClick={handleOpenPreOrderModal}>
                    Pre-order datums
                  </Button>
                }
              >
                {containerShipments.length === 0 ? (
                  <div style={{ padding: "14px 20px", color: "rgba(0, 0, 0, 0.4)", fontSize: 13 }}>
                    Geen andere verzendingen in deze container.
                  </div>
                ) : (
                  <div>
                    {containerShipments.map((cs) => (
                      <div
                        key={cs.id}
                        style={{ padding: "10px 20px", borderBottom: "1px solid rgba(5, 5, 5, 0.04)" }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <RecordLink to={`${urls.purchaseOrderShipmentDetail}${cs.id}`}>{cs.shipmentNumber}</RecordLink>
                          <Tag color={statusColors[cs.status]}>{statusLabels[cs.status]}</Tag>
                        </div>
                        <div style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.4)", marginTop: 3 }}>
                          {cs.supplierName ? `${cs.supplierName} · ` : ""}
                          {(cs.items || []).length} regels
                          {cs.preOrderDatesUpdated ? ` · pre-order ${cs.preOrderAvailableAt}` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            )}
          </>
        }
      >
        <SectionCard
          title={t("products")}
          meta={`${(shipment.items || []).length} regels · ${shipment.totalProducts || 0} stuks`}
          extra={
            <>
              {itemGroups.length > 1 && (
                <Button size="small" onClick={handleToggleAllGroups}>
                  {expandedGroupKeys.length === itemGroups.length ? "Alles inklappen" : "Alles uitklappen"}
                </Button>
              )}
              {!editingShipment && (
                <Button size="small" onClick={handleStartEditingShipment}>
                  Aanpassen
                </Button>
              )}
              {editingShipment && (
                <Button size="small" icon={<PlusOutlined />} onClick={handleOpenAddShipmentItemModal}>
                  Product toevoegen
                </Button>
              )}
            </>
          }
        >
          {unlinkedItemCount > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ margin: "16px 20px 0" }}
              message={`${unlinkedItemCount} regel(s) in deze verzending zijn niet aan een inkooporderregel gekoppeld.`}
              description="Deze regels tellen niet mee in de open aantallen van een order. Verwijder ze en voeg ze opnieuw toe vanuit de inkooporder om de koppeling te herstellen."
            />
          )}

          {costPriceHistory && (
            <Alert
              type="warning"
              showIcon
              style={{ margin: "16px 20px 0" }}
              message={`Kostprijzen bijgewerkt op ${dayjs.unix(costPriceHistory.performedAt).format("DD-MM-YYYY HH:mm")} door ${costPriceHistory.performedBy}`}
              description={`${costPriceHistory.metadata?.updatedCount} artikel(en) aangepast met wisselkoers ${costPriceHistory.newValues.exchangeRate}.`}
            />
          )}

          {/* Regels gegroepeerd per inkooporder. Een zending bundelt regels uit
              meerdere orders; als platte lijst was niet te zien wat bij welke
              order hoort, en stond het ordernummer op elke rij te herhalen. */}
          <Table
            dataSource={itemGroups}
            pagination={false}
            size="small"
            rowKey="key"
            // Zonder scroll.x zet rc-table geen overflow op de tabel en loopt hij bij een
            // smalle hoofdkolom onder de zijkolom door. Een GETAL (geen "max-content"):
            // dat geeft width:Npx; min-width:100%, dus op een breed scherm vult de tabel
            // de kolom en breekt tekst gewoon af.
            // Alleen de BUITENtabel: de geneste tabel in expandedRowRender zit in de
            // <td colSpan> hiervan en scrolt mee. Een eigen scroll.x daar zou een
            // tweede scrollbalk geven.
            scroll={{ x: 720 }}
            expandable={{
              expandedRowKeys: expandedGroupKeys,
              onExpandedRowsChange: (keys) => setExpandedGroupKeys([...keys]),
              expandedRowRender: (group) => (
                <Table
                  dataSource={group.lines}
                  pagination={false}
                  size="small"
                  rowKey="key"
                  showHeader={false}
                  columns={[
                    {
                      title: t("product"),
                      dataIndex: "productName",
                      render: (name, record) => (
                        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                          <span>
                            {name}
                            {record.productColor && (
                              <span style={{ color: "rgba(0, 0, 0, 0.45)", marginLeft: 4 }}>({record.productColor})</span>
                            )}
                          </span>
                          <Num size={12} muted>
                            {record.productCode || "—"}
                          </Num>
                        </div>
                      ),
                    },
                    {
                      title: t("quantity"),
                      dataIndex: "quantity",
                      width: 110,
                      align: "right",
                      render: (value, record) =>
                        editingShipment ? (
                          <InputNumber
                            min={0}
                            value={value}
                            onChange={(v) => handleShipmentItemChange(record._index, "quantity", v || 0)}
                            style={{ width: 80 }}
                          />
                        ) : (
                          <Num>{value}</Num>
                        ),
                    },
                    {
                      title: t("unit_price"),
                      dataIndex: "unitPrice",
                      width: 130,
                      align: "right",
                      render: (value, record) =>
                        editingShipment ? (
                          <InputNumber
                            min={0}
                            step={0.01}
                            prefix={shipmentCurrency}
                            value={value}
                            onChange={(v) => handleShipmentItemChange(record._index, "unitPrice", v || 0)}
                            style={{ width: 110 }}
                          />
                        ) : (
                          <Num muted>{formatPriceByCurrency(value || 0, shipmentCurrency)}</Num>
                        ),
                    },
                    {
                      title: t("total"),
                      width: 120,
                      align: "right",
                      render: (_, record) => (
                        <Num>
                          {formatPriceByCurrency((record.quantity || 0) * (record.unitPrice || 0), shipmentCurrency)}
                        </Num>
                      ),
                    },
                    {
                      key: "actions",
                      width: 44,
                      render: (_, record) =>
                        editingShipment ? (
                          <Button
                            size="small"
                            type="text"
                            danger
                            onClick={() => handleRemoveShipmentItem(record._index)}
                          >
                            ✕
                          </Button>
                        ) : null,
                    },
                  ]}
                />
              ),
            }}
            columns={[
              {
                title: "Inkooporder",
                dataIndex: "purchaseOrderNumber",
                render: (orderNumber, group) =>
                  group.purchaseOrderId ? (
                    <div style={{ display: "flex", alignItems: "baseline", gap: 10, minWidth: 0, flexWrap: "wrap" }}>
                      <RecordLink to={`${urls.purchaseOrdersList}/${group.purchaseOrderId}`}>
                        {orderNumber || group.purchaseOrderId}
                      </RecordLink>
                      <span style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.4)" }}>
                        {group.lines.length} regels · {group.quantity} stuks
                      </span>
                    </div>
                  ) : (
                    <Tag color="orange" icon={<WarningOutlined />}>
                      Niet gekoppeld ({group.lines.length})
                    </Tag>
                  ),
              },
              {
                title: t("quantity"),
                dataIndex: "quantity",
                width: 110,
                align: "right",
                render: (value) => <Num>{value}</Num>,
              },
              { title: "", width: 130 },
              {
                title: t("total"),
                dataIndex: "total",
                width: 120,
                align: "right",
                render: (value) => <Num strong>{formatPriceByCurrency(value, shipmentCurrency)}</Num>,
              },
              { key: "spacer", width: 44 },
            ]}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ background: "#FAFAF9" }}>
                  <Table.Summary.Cell index={0} />
                  <Table.Summary.Cell index={1}>Totaal zending</Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <Num strong>{shipmentTotals.quantity}</Num>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} />
                  <Table.Summary.Cell index={4} align="right">
                    <Num strong size={14}>
                      {formatPriceByCurrency(shipmentTotals.total, shipmentCurrency)}
                    </Num>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </SectionCard>

        {/* Factuur */}
        {invoice && (
          <SectionCard
            title="Factuur"
            meta={invoice.invoiceNumber}
            extra={
              <>
                <Tag color={invoiceStatusColors[invoice.status] || "default"}>
                  {invoiceStatusLabels[invoice.status] || invoice.status || "Ingediend"}
                </Tag>
                <Button size="small" onClick={() => navigate(`${urls.purchaseOrderInvoiceDetail}${invoice.id}`)}>
                  Bekijk factuur
                </Button>
              </>
            }
          >
            <KeyValueList
              labelWidth={160}
              items={[
                {
                  label: "Factuurdatum",
                  value: <Num>{invoice.invoiceDate ? dayjs.unix(invoice.invoiceDate).format("DD-MM-YYYY") : "-"}</Num>,
                },
                {
                  label: "Totaalbedrag",
                  value: (
                    <Num strong size={14}>
                      {formatPriceByCurrency(invoice.totalAmount || 0, shipmentCurrency)}
                    </Num>
                  ),
                },
                { label: "Betaald", value: <Num>{formatPriceByCurrency(invoice.totalPaid || 0, shipmentCurrency)}</Num> },
                {
                  label: "Openstaand",
                  value: (
                    <Num
                      strong
                      style={{ color: (invoice.amountDue ?? invoice.totalAmount ?? 0) > 0 ? "#9E2F2F" : "#2F6B33" }}
                    >
                      {formatPriceByCurrency(invoice.amountDue ?? invoice.totalAmount ?? 0, shipmentCurrency)}
                    </Num>
                  ),
                },
                invoice.exactPurchaseInvoiceId
                  ? { label: "Exact", value: <Tag color="blue">Gesynchroniseerd</Tag> }
                  : null,
              ]}
            />
          </SectionCard>
        )}

        {/* Activiteiten - statuswijzigingen en veldwijzigingen stonden als twee
            losse Collapses onderaan de pagina. Nu één sectie met beide erin. */}
        <SectionCard
          title={t("activity_log")}
          meta={`${(shipment.log || []).length + shipmentHistory.length} gebeurtenissen`}
        >
          <Collapse ghost>
            {shipment.log && shipment.log.length > 0 && (
              <Panel header="Statuswijzigingen" key="1">
                <div style={{ maxHeight: 400, overflowY: "auto", paddingTop: 20 }}>
                  <Timeline>
                    {[...shipment.log].reverse().map((entry, idx) => (
                      <Timeline.Item key={idx}>
                        <div>
                          <p style={{ margin: 0 }}>
                            <strong>{t(`log.${entry.action}`)}</strong>
                          </p>
                          <p style={{ margin: 0, fontSize: 12, color: "#888" }}>
                            {entry.user} - {dayjs.unix(entry.timestamp).format("DD-MM-YYYY HH:mm")}
                          </p>
                          {entry.oldValue && entry.newValue && (
                            <p style={{ margin: 0, fontSize: 12 }}>
                              {statusLabels[entry.oldValue] || entry.oldValue} →{" "}
                              {statusLabels[entry.newValue] || entry.newValue}
                            </p>
                          )}
                        </div>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </div>
              </Panel>
            )}
            <Panel
              header={
                <span>
                  Veldwijzigingen{" "}
                  {!historyLoading && shipmentHistory.length > 0 && (
                    <Tag style={{ marginLeft: 4 }}>{shipmentHistory.length}</Tag>
                  )}
                </span>
              }
              key="history"
            >
              {historyLoading ? (
                <div style={{ textAlign: "center", padding: 24 }}>
                  <Spin />
                </div>
              ) : shipmentHistory.length === 0 ? (
                <div style={{ color: "#999", padding: 8 }}>Nog geen geschiedenis beschikbaar.</div>
              ) : (
                <div style={{ maxHeight: 500, overflowY: "auto", paddingTop: 8 }}>
                  <Timeline>
                    {shipmentHistory.map((entry) => (
                      <Timeline.Item key={entry.id}>
                        <div style={{ marginBottom: 4 }}>
                          <span style={{ fontWeight: 500 }}>{entry.changedBy}</span>
                          <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>
                            {dayjs.unix(entry.changedAt).format("DD-MM-YYYY HH:mm")}
                          </span>
                        </div>
                        {entry.changes?.map((change, idx) => {
                          if (change.field === "shipment_created") {
                            return (
                              <div key={idx} style={{ fontSize: 13, color: "#52c41a" }}>
                                ✓ Verzending aangemaakt
                              </div>
                            );
                          }
                          if (change.field === "status") {
                            return (
                              <div key={idx} style={{ fontSize: 13, marginBottom: 6 }}>
                                Status: <Tag>{statusLabels[change.oldValue] || change.oldValue}</Tag>
                                <ArrowRightOutlined style={{ marginRight: 8 }} />
                                <Tag color="blue">{statusLabels[change.newValue] || change.newValue}</Tag>
                              </div>
                            );
                          }
                          if (change.field === "item_quantity") {
                            return (
                              <div key={idx} style={{ fontSize: 13 }}>
                                <span style={{ color: "#666" }}>
                                  {change.productName} ({change.productCode}):
                                </span>{" "}
                                Aantal{" "}
                                <Tag color={change.newValue < change.oldValue ? "red" : "green"}>
                                  {change.oldValue}x <ArrowRightOutlined style={{ marginRight: 4 }} />
                                  {change.newValue}x
                                </Tag>
                              </div>
                            );
                          }
                          if (change.field === "item_price") {
                            return (
                              <div key={idx} style={{ fontSize: 13 }}>
                                <span style={{ color: "#666" }}>
                                  {change.productName} ({change.productCode}):
                                </span>{" "}
                                Prijs{" "}
                                <Tag>
                                  {change.oldValue} <ArrowRightOutlined style={{ marginRight: 8 }} /> {change.newValue}
                                </Tag>
                              </div>
                            );
                          }
                          if (change.field === "item_added") {
                            return (
                              <div key={idx} style={{ fontSize: 13, color: "#1890ff" }}>
                                + Item toegevoegd: {change.productName} ({change.productCode}) — {change.newValue}x
                              </div>
                            );
                          }
                          if (change.field === "item_removed") {
                            return (
                              <div key={idx} style={{ fontSize: 13, color: "#ff4d4f" }}>
                                − Item verwijderd: {change.productName} ({change.productCode}) — {change.oldValue}x
                              </div>
                            );
                          }
                          if (change.field === "container_number") {
                            return (
                              <div key={idx} style={{ fontSize: 13 }}>
                                Containernummer:{" "}
                                <Tag>
                                  {change.oldValue || "—"} <ArrowRightOutlined style={{ marginRight: 8 }} />{" "}
                                  {change.newValue || "—"}
                                </Tag>
                              </div>
                            );
                          }
                          return (
                            <div key={idx} style={{ fontSize: 12, color: "#888" }}>
                              {change.field}: {JSON.stringify(change.oldValue)}{" "}
                              <ArrowRightOutlined style={{ marginRight: 8 }} /> {JSON.stringify(change.newValue)}
                            </div>
                          );
                        })}
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </div>
              )}
            </Panel>
          </Collapse>
        </SectionCard>
      </DetailLayout>

      {editingShipment && (
        <StickyActionBar note={editSummary}>
          <Button onClick={handleCancelEditingShipment}>Annuleren</Button>
          <Button type="primary" onClick={handleSaveShipmentEdits} loading={saving}>
            Wijzigingen opslaan
          </Button>
        </StickyActionBar>
      )}

        {/* Retry Exact Sync Modal */}
        <Modal
          title={
            <Space>
              <WarningOutlined style={{ color: shipment.exactGoodsReceiptSyncedAt ? "#faad14" : "#1890ff" }} />
              <span>
                {shipment.exactGoodsReceiptSyncedAt ? "Exact opnieuw synchroniseren" : "Synchroniseer naar Exact"}
              </span>
            </Space>
          }
          open={showRetryExactModal}
          onOk={handleRetryExactSync}
          onCancel={() => setShowRetryExactModal(false)}
          okText="Ja, synchroniseer opnieuw"
          cancelText="Annuleren"
          confirmLoading={retryingExactSync}
          okButtonProps={{ danger: !!shipment.exactGoodsReceiptSyncedAt }}
        >
          {shipment.exactGoodsReceiptSyncedAt ? (
            <>
              <p>
                <strong>
                  Deze verzending is al gesynchroniseerd met Exact op{" "}
                  {dayjs.unix(shipment.exactGoodsReceiptSyncedAt).format("DD-MM-YYYY HH:mm")}.
                </strong>
              </p>
              <p style={{ color: "#ff4d4f" }}>
                ⚠️ Als je opnieuw synchroniseert, wordt de voorraadverhoging opnieuw uitgevoerd in Exact. Dit kan leiden
                tot een dubbele voorraadverhoging.
              </p>
              <p>
                Doe dit alleen als de vorige synchronisatie aantoonbaar mislukt is en de voorraad NIET is bijgewerkt in
                Exact.
              </p>
              {shipment.exactGoodsReceiptStatus === "failed" && shipment.exactGoodsReceiptError && (
                <div style={{ padding: 8, backgroundColor: "#fff1f0", borderRadius: 4, marginTop: 8 }}>
                  <strong>Vorige foutmelding:</strong>
                  <div style={{ fontSize: 12, color: "#cf1322", marginTop: 4 }}>{shipment.exactGoodsReceiptError}</div>
                </div>
              )}
            </>
          ) : (
            <p>
              De Exact synchronisatie is nog niet uitgevoerd voor deze verzending. Klik op OK om de goods receipt te
              sturen naar Exact.
            </p>
          )}
        </Modal>

        {/* Ontvangst verwerken — één venster met een stepper, in plaats van drie
            losse modals die elkaar opstartten en onderweg van breedte wisselden.
            De handlers zijn ongewijzigd: alleen de setShow*-aanroepen zijn
            vervangen door setReceiptFlowStep. Belangrijk: de flow is NIET atomair.
            Stap 1 schrijft kostprijzen naar Exact, stap 2 legt de herwaardering
            vast, stap 3 maakt de voorraadtelling. Daarom vraagt sluiten vanaf
            stap 2 om bevestiging en zegt de voettekst wat er al vastligt. */}
        <Modal
          open={receiptFlowStep !== null}
          width={880}
          maskClosable={false}
          onCancel={handleCloseReceiptFlow}
          // Voet lichtgrijs en vast: de body scrolt eronderdoor, zodat de knoppen
          // altijd in beeld staan, ook bij veertig regels.
          // `content: padding 0` is nodig om de voet tot de randen te laten lopen —
          // anders houdt de padding van .ant-modal-content een witte rand om de
          // grijze balk. Kop en body krijgen die padding daarom zelf.
          styles={{
            content: { padding: 0, overflow: "hidden" },
            header: { padding: "20px 24px 0", marginBottom: 0 },
            body: { maxHeight: "58vh", overflowY: "auto", padding: "16px 24px" },
            footer: {
              margin: 0,
              padding: "12px 24px",
              background: "#fafafa",
              borderTop: "1px solid rgba(5, 5, 5, 0.06)",
            },
          }}
          title={
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 16, fontWeight: 500 }}>
                {costPriceRetryMode ? "Kostprijzen opnieuw bijwerken" : "Ontvangst verwerken"}
              </span>
              <Num muted size={12}>
                {shipment.shipmentNumber} · {shipmentTotals.quantity} stuks
              </Num>
              {!costPriceRetryMode && !goodsReceiptIsRetry && (
                <Steps
                  size="small"
                  current={receiptFlowStep ?? 0}
                  style={{ marginTop: 12 }}
                  items={[{ title: "Kostprijzen" }, { title: "Herwaardering" }, { title: "Tellen" }]}
                />
              )}
            </div>
          }
          footer={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <span
                style={{
                  fontSize: 12.5,
                  color: "rgba(0, 0, 0, 0.45)",
                  textAlign: "left",
                  minWidth: 0,
                  flex: "1 1 auto",
                }}
              >
                {receiptFlowStep === 0
                  ? `${costPriceLines.length} artikel${costPriceLines.length === 1 ? "" : "en"} krijgen een nieuwe kostprijs.`
                  : receiptFlowStep === 1
                    ? "Kostprijzen zijn al bijgewerkt in Exact — sluiten draait dat niet terug."
                    : "Verwerken maakt direct een voorraadtelling in Exact; dit is niet ongedaan te maken."}
              </span>
              <Space style={{ flex: "none" }}>
                {receiptFlowStep === 0 && (
                  <>
                    <Button onClick={costPriceRetryMode ? handleCloseReceiptFlow : handleSkipCostPriceUpdate}>
                      {costPriceRetryMode ? "Annuleren" : "Overslaan (alleen tellen)"}
                    </Button>
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      loading={updatingCostPrices}
                      disabled={costPricesAlreadyUpdated && !costPriceRetryMode}
                      onClick={
                        costPricesAlreadyUpdated && !costPriceRetryMode
                          ? handleSkipCostPriceUpdate
                          : handleConfirmCostPriceUpdate
                      }
                    >
                      {costPricesAlreadyUpdated && !costPriceRetryMode
                        ? "Doorgaan naar telling"
                        : `Prijzen bijwerken${costPriceRetryMode ? "" : " & doorgaan"}`}
                    </Button>
                  </>
                )}
                {receiptFlowStep === 1 && (
                  <>
                    <Button onClick={handleCloseReceiptFlow}>Annuleren</Button>
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      loading={savingRevaluationConfirm}
                      disabled={!revaluationConfirmed}
                      onClick={handleConfirmRevaluation}
                    >
                      Ja, herwaardering is doorgevoerd
                    </Button>
                  </>
                )}
                {receiptFlowStep === 2 && (
                  <>
                    <Button onClick={handleCloseReceiptFlow} disabled={processingReceipt}>
                      Annuleren
                    </Button>
                    <Button
                      type="primary"
                      loading={processingReceipt}
                      onClick={
                        shipment.exactStockCountId && !goodsReceiptIsRetry
                          ? handleCloseReceiptFlow
                          : handleProcessStockCount
                      }
                    >
                      {shipment.exactStockCountId && !goodsReceiptIsRetry ? "Sluiten" : "Voorraadtelling verwerken"}
                    </Button>
                  </>
                )}
              </Space>
            </div>
          }
        >
          {receiptFlowStep === 0 && (
          <div>
            {/* Koersveld links, uitleg rechts — zoals in het ontwerp. Het blauwe
                infoblok dat hier stond ("Controleer de nieuwe kostprijzen…") zei
                niets wat de tabel eronder niet al laat zien. */}
            <div
              style={{
                marginBottom: 20,
                display: "flex",
                flexWrap: "wrap",
                gap: 24,
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 12.5, fontWeight: 500 }}>Wisselkoers {shipmentCurrency} &rarr; EUR</label>
                <InputNumber
                  min={0.0001}
                  step={0.001}
                  precision={4}
                  value={costPriceExchangeRate}
                  onChange={handleExchangeRateChange}
                  style={{ width: 160 }}
                />
                <span style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.45)" }}>
                  Gewogen gemiddelde over alle betalingen.
                </span>
              </div>
              <span
                style={{ fontSize: 12.5, color: "rgba(0, 0, 0, 0.45)", maxWidth: 330, lineHeight: 1.5, flex: "1 1 260px" }}
              >
                Inkoopprijs &times; koers wordt de nieuwe kostprijs in Exact. Je kunt dit overslaan en alleen tellen.
              </span>
            </div>

            {/* Weighted Exchange Rate Overview */}
            {allInvoicePayments.length > 0 && (
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#f6ffed", borderRadius: 8 }}>
                {(() => {
                  const relevantPayments = allInvoicePayments.filter((p) => p.exchangeRate);
                  let totalAmount = 0;
                  let weightedRateSum = 0;
                  for (const p of relevantPayments) {
                    const amount = Number(p.amount) || 0;
                    const rate = Number(p.exchangeRate) || 1;
                    totalAmount += amount;
                    weightedRateSum += amount * rate;
                  }
                  const currentWeightedRate = totalAmount > 0 ? weightedRateSum / totalAmount : 0;

                  // Group by invoice
                  const invoiceGroups = [];
                  const seen = new Set();
                  for (const p of relevantPayments) {
                    const key = `${p._invoiceType}__${p._invoiceNumber}`;
                    if (!seen.has(key)) {
                      seen.add(key);
                      invoiceGroups.push({ type: p._invoiceType, number: p._invoiceNumber });
                    }
                  }

                  return (
                    <div>
                      <div style={{ fontSize: 12, marginBottom: 8 }}>
                        <strong>Berekening:</strong>
                      </div>
                      {invoiceGroups.map(({ type, number }) => {
                        const groupPayments = relevantPayments.filter(
                          (p) => p._invoiceType === type && p._invoiceNumber === number,
                        );
                        return (
                          <div key={`${type}__${number}`} style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#444", marginBottom: 3 }}>
                              <Tag color={type === "proforma" ? "orange" : "blue"} style={{ fontSize: 10 }}>
                                {type === "proforma" ? "Pro forma" : "Definitief"}
                              </Tag>
                              {number}
                            </div>
                            {groupPayments.map((payment, idx) => (
                              <div key={idx} style={{ fontSize: 11, color: "#666", marginBottom: 2, paddingLeft: 8 }}>
                                - {formatPriceByCurrency(payment.amount, shipmentCurrency)} × {payment.exchangeRate} = €
                                {(payment.amount * payment.exchangeRate).toFixed(2)}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                      <div
                        style={{
                          fontSize: 11,
                          color: "#666",
                          marginTop: 4,
                          paddingTop: 4,
                          borderTop: "1px solid #d9f7be",
                        }}
                      >
                        <strong>
                          Totaal: {formatPriceByCurrency(totalAmount, shipmentCurrency)} → €{weightedRateSum.toFixed(2)}
                        </strong>
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#52c41a", marginTop: 4 }}>
                        Gewogen koers: {currentWeightedRate.toFixed(4)}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Zelfde opmaak als de tabellen op de andere inkooppagina's: productnaam
                met de code eronder, bedragen rechts uitgelijnd in Num, scroll.x zodat
                hij niet buiten zijn kaart loopt. Kolommen uit het ontwerp:
                Inkoop · Huidig EUR · Nieuw EUR. De per-regel wisselkoers zat als
                eigen kolom in de weg; die staat nu boven de tabel en is per regel
                nog aan te passen via de nieuwe kostprijs zelf. */}
            <Table
              dataSource={costPriceLines.map((line, i) => ({ ...line, key: i, _index: i }))}
              pagination={false}
              size="small"
              scroll={{ x: 620 }}
              columns={[
                {
                  title: t("product"),
                  dataIndex: "productName",
                  render: (name, record) => (
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                      <span style={{ fontWeight: 500 }}>{name}</span>
                      <Num size={12} muted>
                        {record.itemCode}
                      </Num>
                    </div>
                  ),
                },
                {
                  title: "Inkoop",
                  dataIndex: "unitPriceOriginal",
                  width: 110,
                  align: "right",
                  render: (v) => <Num muted>{formatPriceByCurrency(v || 0, shipmentCurrency)}</Num>,
                },
                {
                  title: "Huidig EUR",
                  dataIndex: "currentCostPriceEur",
                  width: 110,
                  align: "right",
                  render: (v) => (v != null ? <Num muted>€ {Number(v).toFixed(2)}</Num> : <Num muted>—</Num>),
                },
                {
                  title: "Nieuw EUR",
                  dataIndex: "_index",
                  width: 150,
                  align: "right",
                  render: (idx) => (
                    <InputNumber
                      min={0}
                      step={0.01}
                      precision={2}
                      prefix="€"
                      value={costPriceLines[idx]?.newCostPriceEur ?? 0}
                      onChange={(v) => handleCostPriceLineChange(idx, "newCostPriceEur", v || 0)}
                      style={{ width: "100%" }}
                    />
                  ),
                },
              ]}
            />
          </div>
          )}
          {receiptFlowStep === 1 && (
            <>
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                marginBottom: 16,
                padding: 16,
                backgroundColor: "#fff7e6",
                borderRadius: 8,
                border: "1px solid #ffe58f",
              }}
            >
              <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: "#d48806" }}>
                ⚠️ Handmatige actie vereist in Exact Online
              </p>
              <p style={{ margin: "12px 0 0 0", fontSize: 13, color: "#666" }}>
                De kostprijzen zijn bijgewerkt in Exact. Voordat de voorraadtelling kan worden verwerkt, moet je eerst{" "}
                <strong>handmatig de herwaardering van de artikelen doorvoeren in Exact Online</strong>.
              </p>
              <p style={{ margin: "12px 0 0 0", fontSize: 13, color: "#666" }}>
                Ga naar Exact Online en activeer de nieuwe kostprijzen. Bevestig hieronder pas als dit is gedaan.
              </p>
            </div>
          </div>
              {/* Expliciete bevestiging in plaats van alleen het knoplabel: dit legt
                  met tijdstempel en naam vast dat de herwaardering echt is gedaan. */}
              <Checkbox
                checked={revaluationConfirmed}
                onChange={(e) => setRevaluationConfirmed(e.target.checked)}
                style={{ marginTop: 16 }}
              >
                De herwaardering is doorgevoerd in Exact
              </Checkbox>
            </>
          )}
          {receiptFlowStep === 2 && (
          <div style={{ marginTop: 16 }}>
            {shipment.exactStockCountId && (
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#fff7e6", borderRadius: 8 }}>
                <p style={{ margin: 0, fontWeight: 500, color: "#d48806" }}>
                  ⚠️ <strong>Let op:</strong> Deze verzending is al verwerkt in Exact Online.
                </p>
                <p style={{ margin: "4px 0 0 0", fontSize: 12, color: "#d48806" }}>
                  Exact Stock Count ID: {shipment.exactStockCountId}
                  {shipment.exactStockCountSyncedAt && (
                    <span>
                      {" "}
                      • Verwerkt op: {dayjs.unix(shipment.exactStockCountSyncedAt).format("DD-MM-YYYY HH:mm")}
                    </span>
                  )}
                </p>
              </div>
            )}
            <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#e6f7ff", borderRadius: 8 }}>
              <p style={{ margin: 0, fontWeight: 500 }}>ℹ️ Vul hieronder de getelde aantallen in per product.</p>
              <p style={{ margin: "4px 0 0 0", fontSize: 12, color: "#666" }}>
                Na verwerking wordt de voorraad automatisch bijgewerkt in Exact Online en ontvangt de boekhouder een
                email.
              </p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Ontvangstdatum <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <DatePicker style={{ width: "100%" }} value={receiptDate} onChange={setReceiptDate} format="DD-MM-YYYY" />
            </div>

            <h5 style={{ marginBottom: 12 }}>Producten</h5>
            <Table
              dataSource={(shipment.items || []).map((item, index) => ({ ...item, index, key: index }))}
              pagination={false}
              size="small"
              columns={[
                {
                  title: "Product",
                  dataIndex: "productName",
                  render: (name, record) => (
                    <div>
                      <div style={{ fontWeight: 500 }}>{name}</div>
                      <div style={{ fontSize: 11, color: "#888" }}>Code: {record.productCode || "-"}</div>
                    </div>
                  ),
                },
                {
                  title: "Verzonden",
                  dataIndex: "quantity",
                  width: 100,
                  render: (qty, record) => {
                    const invoiceQuantity = getInvoiceQuantity(record.index);
                    const hasDiscrepancy = invoiceQuantity !== null && invoiceQuantity !== qty;
                    return (
                      <div>
                        <div style={{ fontWeight: 500 }}>{qty}</div>
                        {hasDiscrepancy && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#ff4d4f",
                              marginTop: 2,
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <WarningOutlined style={{ fontSize: 10 }} />
                            <span>Op factuur: {invoiceQuantity}</span>
                          </div>
                        )}
                      </div>
                    );
                  },
                },
                {
                  title: "Geteld Aantal",
                  dataIndex: "index",
                  width: 150,
                  render: (index, record) => (
                    <InputNumber
                      min={0}
                      value={receivedQuantities[index]}
                      onChange={(value) => setReceivedQuantities((prev) => ({ ...prev, [index]: value }))}
                      style={{ width: "100%" }}
                      status={receivedQuantities[index] !== record.quantity ? "warning" : undefined}
                    />
                  ),
                },
                {
                  title: "Verschil",
                  dataIndex: "index",
                  width: 100,
                  render: (index, record) => {
                    const diff = (receivedQuantities[index] || 0) - record.quantity;
                    return (
                      <span
                        style={{ color: diff === 0 ? "#52c41a" : diff < 0 ? "#ff4d4f" : "#faad14", fontWeight: 500 }}
                      >
                        {diff > 0 ? "+" : ""}
                        {diff}
                      </span>
                    );
                  },
                },
              ]}
            />

            {!shipment.exactStockCountId && (
              <div style={{ marginTop: 16, padding: 12, backgroundColor: "#fff7e6", borderRadius: 8 }}>
                <p style={{ margin: 0, fontSize: 12, color: "#d48806" }}>
                  ⚠️ <strong>Let op:</strong> Deze actie kan niet ongedaan worden gemaakt. De voorraad wordt direct
                  bijgewerkt in Exact Online.
                </p>
              </div>
            )}
          </div>
          )}
        </Modal>

        {/* Add Shipment Item Modal — order lines only (BMX-18), multiple at once */}
        <Modal
          title="Producten toevoegen vanuit inkooporder"
          open={showAddShipmentItemModal}
          onOk={handleAddShipmentItems}
          onCancel={handleCloseAddShipmentItemModal}
          okText={selectedOrderLines.length > 1 ? `${selectedOrderLines.length} producten toevoegen` : "Toevoegen"}
          cancelText="Annuleren"
          okButtonProps={{ disabled: selectedOrderLines.length === 0 }}
          width={1000}
        >
          <div style={{ marginTop: 16 }}>
            {availableOrderLines.length === 0 ? (
              <Alert
                type="warning"
                showIcon
                message="Geen orderregels beschikbaar"
                description={
                  shipment.sourceOrders?.length
                    ? "De gekoppelde inkooporder(s) bevatten geen producten. Voeg het product eerst toe aan de inkooporder; daarna kun je het hier selecteren."
                    : "Deze verzending heeft geen gekoppelde inkooporder, dus er zijn geen orderregels om uit te kiezen."
                }
              />
            ) : (
              <div>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Je kunt alleen producten kiezen die op de gekoppelde inkooporder(s) staan."
                  description="Vink meerdere regels aan om ze in één keer toe te voegen. Staat een product er niet tussen? Voeg het dan eerst toe aan de inkooporder zelf."
                />

                <Input
                  prefix={<SearchOutlined />}
                  placeholder="Zoek op productnaam, code of ordernummer"
                  value={addItemSearch}
                  onChange={(e) => setAddItemSearch(e.target.value)}
                  allowClear
                  style={{ marginBottom: 12 }}
                />

                <Table
                  size="small"
                  rowKey="key"
                  pagination={false}
                  scroll={{ x: true, y: 500 }}
                  dataSource={availableOrderLines.filter((line) => {
                    const term = addItemSearch.trim().toLowerCase();
                    if (!term) return true;
                    return (
                      line.productName?.toLowerCase().includes(term) ||
                      line.productCode?.toLowerCase().includes(term) ||
                      line.purchaseOrderNumber?.toLowerCase().includes(term)
                    );
                  })}
                  rowSelection={{
                    type: "checkbox",
                    selectedRowKeys: selectedOrderLineKeys,
                    onChange: handleSelectionChange,
                    getCheckboxProps: (record) => ({
                      // Already on this shipment: change the quantity in the
                      // overview instead of adding a second line for it.
                      disabled: record.inThisShipment > 0,
                    }),
                  }}
                  onRow={(record) => ({
                    onClick: () => {
                      if (record.inThisShipment === 0) handleToggleOrderLine(record.key);
                    },
                    style: { cursor: record.inThisShipment > 0 ? "not-allowed" : "pointer" },
                  })}
                  columns={[
                    {
                      title: "Inkooporder",
                      dataIndex: "purchaseOrderNumber",
                      render: (v) => <Tag color="blue">{v}</Tag>,
                    },
                    {
                      title: t("product"),
                      dataIndex: "productName",
                      render: (name, record) => (
                        <span>
                          {name}
                          {record.productColor && (
                            <span style={{ color: "#666", marginLeft: 4 }}>({record.productColor})</span>
                          )}
                        </span>
                      ),
                    },
                    { title: "Code", dataIndex: "productCode", render: (v) => v || "-" },
                    { title: "Besteld", dataIndex: "ordered", align: "right", width: 70 },
                    {
                      title: "Verzonden",
                      dataIndex: "shipped",
                      align: "right",
                      width: 90,
                      render: (v) => <span style={{ color: v > 0 ? "#1890ff" : "#999" }}>{v}</span>,
                    },
                    {
                      title: "Nog open",
                      dataIndex: "open",
                      align: "right",
                      render: (v, record) => (
                        <Space direction="vertical" size={0} style={{ alignItems: "flex-end" }}>
                          {record.inThisShipment > 0 ? (
                            <Tag color="green">Al in deze verzending ({record.inThisShipment})</Tag>
                          ) : v > 0 ? (
                            <strong style={{ color: "#fa8c16" }}>{v}</strong>
                          ) : (
                            <Tag color="default">
                              {record.shipped >= record.ordered - record.cancelledAmount
                                ? "Volledig verzonden"
                                : "Niets meer te versturen"}
                            </Tag>
                          )}
                          {record.cancelledAmount > 0 && (
                            <Tag color="red" style={{ marginTop: 2 }}>
                              {record.cancelledAmount} geannuleerd
                            </Tag>
                          )}
                        </Space>
                      ),
                    },
                    {
                      title: "Aantal",
                      key: "inputQuantity",
                      // Clicks inside the inputs must not toggle the row selection.
                      onCell: () => ({ onClick: (e) => e.stopPropagation() }),
                      render: (_, record) => {
                        const isSelected = selectedOrderLineKeys.includes(record.key);
                        return (
                          <InputNumber
                            size="small"
                            style={{ width: "100%" }}
                            min={1}
                            disabled={!isSelected}
                            value={isSelected ? lineInputs[record.key]?.quantity : null}
                            onChange={(v) => handleLineInputChange(record.key, "quantity", v)}
                            status={
                              isSelected && lineInputs[record.key]?.quantity > record.open ? "warning" : undefined
                            }
                          />
                        );
                      },
                    },
                    {
                      title: `Prijs (${shipmentCurrency})`,
                      key: "inputPrice",
                      onCell: () => ({ onClick: (e) => e.stopPropagation() }),
                      render: (_, record) => {
                        const isSelected = selectedOrderLineKeys.includes(record.key);
                        return (
                          <InputNumber
                            size="small"
                            style={{ width: "100%" }}
                            min={0.01}
                            step={0.01}
                            precision={2}
                            disabled={!isSelected}
                            value={isSelected ? lineInputs[record.key]?.unitPrice : null}
                            onChange={(v) => handleLineInputChange(record.key, "unitPrice", v)}
                          />
                        );
                      },
                    },
                  ]}
                />

                {selectedOrderLines.length > 0 && (
                  <div
                    style={{
                      marginTop: 16,
                      padding: 12,
                      backgroundColor: "#f6ffed",
                      borderRadius: 8,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ fontSize: 13, color: "#666" }}>
                      {selectedOrderLines.length} regel(s) geselecteerd ·{" "}
                      {selectedOrderLines.reduce((sum, l) => sum + (lineInputs[l.key]?.quantity || 0), 0)} stuks
                    </span>
                    <span style={{ fontSize: 16, fontWeight: "bold", color: "#52c41a" }}>
                      {formatPriceByCurrency(
                        selectedOrderLines.reduce(
                          (sum, l) => sum + (lineInputs[l.key]?.quantity || 0) * (lineInputs[l.key]?.unitPrice || 0),
                          0,
                        ),
                        shipmentCurrency,
                      )}
                    </span>
                  </div>
                )}

                {selectedOrderLines.some((l) => (lineInputs[l.key]?.quantity || 0) > l.open) && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 12 }}
                    message="Je verzendt van sommige regels meer dan er nog openstaat"
                    description={
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {selectedOrderLines
                          .filter((l) => (lineInputs[l.key]?.quantity || 0) > l.open)
                          .map((l) => (
                            <li key={l.key}>
                              {l.productName} ({l.purchaseOrderNumber}): {lineInputs[l.key]?.quantity} van {l.open} nog
                              open
                            </li>
                          ))}
                      </ul>
                    }
                  />
                )}
              </div>
            )}
          </div>
        </Modal>

        {/*/!* Add Invoice Line Modal *!/*/}
        {/*<Modal*/}
        {/*  title="Factuurregel toevoegen"*/}
        {/*  open={showAddLineModal}*/}
        {/*  onOk={handleAddInvoiceLine}*/}
        {/*  onCancel={handleCloseAddLineModal}*/}
        {/*  okText="Toevoegen"*/}
        {/*  cancelText="Annuleren"*/}
        {/*  width={600}*/}
        {/*>*/}
        {/*  <div style={{ marginTop: 16 }}>*/}
        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>*/}
        {/*        Product <span style={{ color: "#ff4d4f" }}>*</span>*/}
        {/*      </label>*/}
        {/*      <Select*/}
        {/*        style={{ width: "100%" }}*/}
        {/*        placeholder={*/}
        {/*          products.length === 0 ? "Geen producten beschikbaar voor deze leverancier" : "Selecteer een product"*/}
        {/*        }*/}
        {/*        value={selectedProduct?.id}*/}
        {/*        onChange={handleProductSelect}*/}
        {/*        showSearch*/}
        {/*        filterOption={(input, option) => {*/}
        {/*          const product = products.find((p) => p.id === option.value);*/}
        {/*          if (!product) return false;*/}
        {/*          const searchText = input.toLowerCase();*/}
        {/*          return (*/}
        {/*            product.name.toLowerCase().includes(searchText) ||*/}
        {/*            (product.sku && product.sku.toLowerCase().includes(searchText)) ||*/}
        {/*            (product.code && product.code.toLowerCase().includes(searchText))*/}
        {/*          );*/}
        {/*        }}*/}
        {/*        disabled={products.length === 0}*/}
        {/*      >*/}
        {/*        {products*/}
        {/*          .filter((product) => {*/}
        {/*            const alreadyInLines = editedInvoiceLines.some(*/}
        {/*              (line) =>*/}
        {/*                line.productId === product.id ||*/}
        {/*                (line.productCode && line.productCode === (product.sku || product.code)),*/}
        {/*            );*/}
        {/*            return !alreadyInLines;*/}
        {/*          })*/}
        {/*          .map((product) => (*/}
        {/*            <Select.Option key={product.id} value={product.id}>*/}
        {/*              <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 6 }}>*/}
        {/*                <span style={{ fontWeight: 500 }}>{product.name}</span>*/}
        {/*                <span style={{ fontSize: 11, color: "#888" }}>{product.code || ""}</span>*/}
        {/*              </div>*/}
        {/*            </Select.Option>*/}
        {/*          ))}*/}
        {/*      </Select>*/}
        {/*    </div>*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>*/}
        {/*        Hoeveelheid <span style={{ color: "#ff4d4f" }}>*</span>*/}
        {/*      </label>*/}
        {/*      <InputNumber*/}
        {/*        style={{ width: "100%" }}*/}
        {/*        min={1}*/}
        {/*        value={newLineQuantity}*/}
        {/*        onChange={setNewLineQuantity}*/}
        {/*        placeholder="1"*/}
        {/*      />*/}
        {/*    </div>*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>*/}
        {/*        Eenheidsprijs ({shipmentCurrency}) <span style={{ color: "#ff4d4f" }}>*</span>*/}
        {/*      </label>*/}
        {/*      <InputNumber*/}
        {/*        style={{ width: "100%" }}*/}
        {/*        min={0.01}*/}
        {/*        step={0.01}*/}
        {/*        precision={2}*/}
        {/*        prefix={shipmentCurrency}*/}
        {/*        value={newLineUnitPrice}*/}
        {/*        onChange={setNewLineUnitPrice}*/}
        {/*        placeholder="0.00"*/}
        {/*      />*/}
        {/*    </div>*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Korting (%)</label>*/}
        {/*      <InputNumber*/}
        {/*        style={{ width: "100%" }}*/}
        {/*        min={0}*/}
        {/*        max={100}*/}
        {/*        value={newLineDiscount}*/}
        {/*        onChange={setNewLineDiscount}*/}
        {/*        placeholder="0"*/}
        {/*        suffix="%"*/}
        {/*      />*/}
        {/*    </div>*/}

        {/*    {newLineQuantity && newLineUnitPrice && (*/}
        {/*      <div style={{ padding: 12, backgroundColor: "#f6ffed", borderRadius: 8 }}>*/}
        {/*        <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Totaal (excl. korting):</div>*/}
        {/*        <div style={{ fontSize: 14, fontWeight: 500 }}>*/}
        {/*          {formatPriceByCurrency(newLineQuantity * newLineUnitPrice, shipmentCurrency)}*/}
        {/*        </div>*/}
        {/*        {newLineDiscount > 0 && (*/}
        {/*          <>*/}
        {/*            <div style={{ fontSize: 12, color: "#666", marginTop: 4, marginBottom: 4 }}>Korting:</div>*/}
        {/*            <div style={{ fontSize: 14, color: "#ff4d4f" }}>*/}
        {/*              -*/}
        {/*              {formatPriceByCurrency(*/}
        {/*                newLineQuantity * newLineUnitPrice * (newLineDiscount / 100),*/}
        {/*                shipmentCurrency,*/}
        {/*              )}*/}
        {/*            </div>*/}
        {/*          </>*/}
        {/*        )}*/}
        {/*        <div style={{ fontSize: 12, color: "#666", marginTop: 8, marginBottom: 4 }}>*/}
        {/*          Totaal (incl. korting):*/}
        {/*        </div>*/}
        {/*        <div style={{ fontSize: 16, fontWeight: "bold", color: "#52c41a" }}>*/}
        {/*          {formatPriceByCurrency(*/}
        {/*            newLineQuantity * newLineUnitPrice * (1 - newLineDiscount / 100),*/}
        {/*            shipmentCurrency,*/}
        {/*          )}*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*    )}*/}
        {/*  </div>*/}
        {/*</Modal>*/}

        {/*/!* Payment Modal *!/*/}
        {/*<Modal*/}
        {/*  title="Betaling toevoegen"*/}
        {/*  open={showPaymentModal}*/}
        {/*  onOk={handleAddPayment}*/}
        {/*  onCancel={() => {*/}
        {/*    setShowPaymentModal(false);*/}
        {/*    resetPaymentForm();*/}
        {/*  }}*/}
        {/*  okText="Betaling opslaan"*/}
        {/*  confirmLoading={savingPayment}*/}
        {/*>*/}
        {/*  <div style={{ marginTop: 16 }}>*/}
        {/*    {invoice && (*/}
        {/*      <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#e6f7ff", borderRadius: 8 }}>*/}
        {/*        <div>*/}
        {/*          Factuur: <strong>{invoice.invoiceNumber}</strong>*/}
        {/*        </div>*/}
        {/*        <div>*/}
        {/*          Totaal: <strong>{formatPriceByCurrency(invoice.totalAmount || 0, shipmentCurrency)}</strong>*/}
        {/*        </div>*/}
        {/*        <div>*/}
        {/*          Reeds betaald: <strong>{formatPriceByCurrency(invoice.totalPaid || 0, shipmentCurrency)}</strong>*/}
        {/*        </div>*/}
        {/*        <div>*/}
        {/*          Openstaand:{" "}*/}
        {/*          <strong style={{ color: "#ff4d4f" }}>*/}
        {/*            {formatPriceByCurrency(invoice.amountDue ?? invoice.totalAmount ?? 0, shipmentCurrency)}*/}
        {/*          </strong>*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*    )}*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>*/}
        {/*        Bedrag ({shipmentCurrency}) <span style={{ color: "#ff4d4f" }}>*</span>*/}
        {/*      </label>*/}
        {/*      <InputNumber*/}
        {/*        style={{ width: "100%" }}*/}
        {/*        min={0.01}*/}
        {/*        step={0.01}*/}
        {/*        precision={2}*/}
        {/*        value={paymentAmount}*/}
        {/*        onChange={setPaymentAmount}*/}
        {/*        placeholder="0.00"*/}
        {/*      />*/}
        {/*      {paymentAmount && paymentExchangeRate && (*/}
        {/*        <div style={{ marginTop: 4, fontSize: 12, color: "#666" }}>*/}
        {/*          ≈ €{(paymentAmount * paymentExchangeRate).toFixed(2)}*/}
        {/*        </div>*/}
        {/*      )}*/}
        {/*    </div>*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>*/}
        {/*        Wisselkoers ({shipmentCurrency} → EUR) <span style={{ color: "#ff4d4f" }}>*</span>*/}
        {/*      </label>*/}
        {/*      <InputNumber*/}
        {/*        style={{ width: "100%" }}*/}
        {/*        min={0.0001}*/}
        {/*        step={0.0001}*/}
        {/*        precision={4}*/}
        {/*        value={paymentExchangeRate}*/}
        {/*        onChange={setPaymentExchangeRate}*/}
        {/*        placeholder={shipmentCurrency === "IDR" ? "bijv. 0.00006" : "bijv. 0.8500"}*/}
        {/*      />*/}
        {/*      <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>Conversie naar euro voor administratie</div>*/}
        {/*    </div>*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>*/}
        {/*        Betaaldatum <span style={{ color: "#ff4d4f" }}>*</span>*/}
        {/*      </label>*/}
        {/*      <DatePicker style={{ width: "100%" }} value={paymentDate} onChange={setPaymentDate} format="DD-MM-YYYY" />*/}
        {/*    </div>*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Referentie / Transactie ID</label>*/}
        {/*      <Input*/}
        {/*        value={paymentReference}*/}
        {/*        onChange={(e) => setPaymentReference(e.target.value)}*/}
        {/*        placeholder="bijv. TXN-123456"*/}
        {/*      />*/}
        {/*    </div>*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Notities</label>*/}
        {/*      <TextArea*/}
        {/*        rows={2}*/}
        {/*        value={paymentNotes}*/}
        {/*        onChange={(e) => setPaymentNotes(e.target.value)}*/}
        {/*        placeholder="Optionele opmerkingen"*/}
        {/*      />*/}
        {/*    </div>*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>*/}
        {/*        Betalingsbewijs (PDF/afbeelding)*/}
        {/*      </label>*/}
        {/*      <Upload*/}
        {/*        accept=".pdf,.jpg,.jpeg,.png"*/}
        {/*        maxCount={1}*/}
        {/*        beforeUpload={(file) => {*/}
        {/*          if (file.size > 10 * 1024 * 1024) {*/}
        {/*            message.error("Bestand moet kleiner zijn dan 10MB");*/}
        {/*            return Upload.LIST_IGNORE;*/}
        {/*          }*/}
        {/*          setPaymentProofFile(file);*/}
        {/*          return false;*/}
        {/*        }}*/}
        {/*        onRemove={() => setPaymentProofFile(null)}*/}
        {/*        fileList={*/}
        {/*          paymentProofFile*/}
        {/*            ? [{ uid: "-1", name: paymentProofFile.name, status: "done", size: paymentProofFile.size }]*/}
        {/*            : []*/}
        {/*        }*/}
        {/*      >*/}
        {/*        <Button icon={<UploadOutlined />}>Selecteer bestand</Button>*/}
        {/*      </Upload>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</Modal>*/}

        {/*/!* Edit Payment Modal *!/*/}
        {/*<Modal*/}
        {/*  title="Betaling bewerken"*/}
        {/*  open={showEditPaymentModal}*/}
        {/*  onOk={handleUpdatePayment}*/}
        {/*  onCancel={() => {*/}
        {/*    setShowEditPaymentModal(false);*/}
        {/*    setEditingPayment(null);*/}
        {/*    resetPaymentForm();*/}
        {/*  }}*/}
        {/*  okText="Wijzigingen opslaan"*/}
        {/*  confirmLoading={savingPayment}*/}
        {/*>*/}
        {/*  <div style={{ marginTop: 16 }}>*/}
        {/*    {editingPayment && (*/}
        {/*      <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#e6f7ff", borderRadius: 8 }}>*/}
        {/*        <div>*/}
        {/*          Factuur: <strong>{invoice?.invoiceNumber}</strong>*/}
        {/*        </div>*/}
        {/*        <div>*/}
        {/*          Huidig bedrag: <strong>{formatPriceByCurrency(editingPayment.amount || 0, shipmentCurrency)}</strong>*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*    )}*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>*/}
        {/*        Bedrag ({shipmentCurrency}) <span style={{ color: "#ff4d4f" }}>*</span>*/}
        {/*      </label>*/}
        {/*      <InputNumber*/}
        {/*        style={{ width: "100%" }}*/}
        {/*        min={0.01}*/}
        {/*        step={0.01}*/}
        {/*        precision={2}*/}
        {/*        value={paymentAmount}*/}
        {/*        onChange={setPaymentAmount}*/}
        {/*        placeholder="0.00"*/}
        {/*      />*/}
        {/*      {paymentAmount && paymentExchangeRate && (*/}
        {/*        <div style={{ marginTop: 4, fontSize: 12, color: "#666" }}>*/}
        {/*          ≈ €{(paymentAmount * paymentExchangeRate).toFixed(2)}*/}
        {/*        </div>*/}
        {/*      )}*/}
        {/*    </div>*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>*/}
        {/*        Wisselkoers ({shipmentCurrency} → EUR) <span style={{ color: "#ff4d4f" }}>*</span>*/}
        {/*      </label>*/}
        {/*      <InputNumber*/}
        {/*        style={{ width: "100%" }}*/}
        {/*        min={0.0001}*/}
        {/*        step={0.0001}*/}
        {/*        precision={4}*/}
        {/*        value={paymentExchangeRate}*/}
        {/*        onChange={setPaymentExchangeRate}*/}
        {/*        placeholder={shipmentCurrency === "IDR" ? "bijv. 0.00006" : "bijv. 0.8500"}*/}
        {/*      />*/}
        {/*      <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>Conversie naar euro voor administratie</div>*/}
        {/*    </div>*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>*/}
        {/*        Betaaldatum <span style={{ color: "#ff4d4f" }}>*</span>*/}
        {/*      </label>*/}
        {/*      <DatePicker style={{ width: "100%" }} value={paymentDate} onChange={setPaymentDate} format="DD-MM-YYYY" />*/}
        {/*    </div>*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Referentie / Transactie ID</label>*/}
        {/*      <Input*/}
        {/*        value={paymentReference}*/}
        {/*        onChange={(e) => setPaymentReference(e.target.value)}*/}
        {/*        placeholder="bijv. TXN-123456"*/}
        {/*      />*/}
        {/*    </div>*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Notities</label>*/}
        {/*      <TextArea*/}
        {/*        rows={2}*/}
        {/*        value={paymentNotes}*/}
        {/*        onChange={(e) => setPaymentNotes(e.target.value)}*/}
        {/*        placeholder="Optionele opmerkingen"*/}
        {/*      />*/}
        {/*    </div>*/}

        {/*    <div style={{ marginBottom: 16 }}>*/}
        {/*      <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>*/}
        {/*        Betalingsbewijs (PDF/afbeelding)*/}
        {/*      </label>*/}
        {/*      {editingPayment?.proofUrl && (*/}
        {/*        <div style={{ marginBottom: 8 }}>*/}
        {/*          <a href={editingPayment.proofUrl} target="_blank" rel="noopener noreferrer">*/}
        {/*            <Button size="small" type="link" style={{ padding: 0 }}>*/}
        {/*              Huidig bewijs bekijken*/}
        {/*            </Button>*/}
        {/*          </a>*/}
        {/*        </div>*/}
        {/*      )}*/}
        {/*      <Upload*/}
        {/*        accept=".pdf,.jpg,.jpeg,.png"*/}
        {/*        maxCount={1}*/}
        {/*        beforeUpload={(file) => {*/}
        {/*          if (file.size > 10 * 1024 * 1024) {*/}
        {/*            message.error("Bestand moet kleiner zijn dan 10MB");*/}
        {/*            return Upload.LIST_IGNORE;*/}
        {/*          }*/}
        {/*          setPaymentProofFile(file);*/}
        {/*          return false;*/}
        {/*        }}*/}
        {/*        onRemove={() => setPaymentProofFile(null)}*/}
        {/*        fileList={*/}
        {/*          paymentProofFile*/}
        {/*            ? [{ uid: "-1", name: paymentProofFile.name, status: "done", size: paymentProofFile.size }]*/}
        {/*            : []*/}
        {/*        }*/}
        {/*      >*/}
        {/*        <Button icon={<UploadOutlined />}>*/}
        {/*          {editingPayment?.proofUrl ? "Vervang bewijs" : "Selecteer bestand"}*/}
        {/*        </Button>*/}
        {/*      </Upload>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</Modal>*/}

        {/* Pre-order Modal */}
        <Modal
          title="Pre-order datums instellen"
          width={720}
          styles={{ body: { maxHeight: "60vh", overflowY: "auto" } }}
          open={showPreOrderModal}
          onOk={handleSavePreOrder}
          onCancel={() => setShowPreOrderModal(false)}
          okText="Datums instellen"
          confirmLoading={savingPreOrder}
        >
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#e6f7ff", borderRadius: 8 }}>
            <div>
              Container: <strong>{shipment.containerNumber}</strong>
            </div>
            <div style={{ marginTop: 4, fontSize: 13, color: "#555" }}>
              Alle producten in de geselecteerde verzendingen worden op pre-order gezet met de gekozen datum als
              beschikbaarheidsdatum.
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
              Aankomstdatum <span style={{ color: "#ff4d4f" }}>*</span>
            </label>
            <DatePicker
              style={{ width: "100%" }}
              value={preOrderDate}
              onChange={setPreOrderDate}
              format="DD-MM-YYYY"
              placeholder="Selecteer aankomstdatum"
              disabledDate={(current) => current && current < dayjs().startOf("day")}
            />
          </div>

          <div style={{ marginBottom: 8, fontWeight: 500 }}>Verzendingen in deze container:</div>
          {/* Was een handgemaakte lijst met per zending alle productnamen als één
              komma-alinea van ~40 namen. Nu een tabel met selectie per zending en de
              producten achter de uitklap. De selectie voedt exact dezelfde
              preOrderShipmentIds; handleSavePreOrder is ongewijzigd. */}
          <Table
            size="small"
            pagination={false}
            scroll={{ x: 420 }}
            dataSource={[
              {
                id,
                shipmentNumber: shipment.shipmentNumber,
                supplierName: shipment.supplierName,
                items: shipment.items,
              },
              ...containerShipments,
            ]}
            rowKey="id"
            rowSelection={{
              selectedRowKeys: preOrderShipmentIds,
              onChange: (keys) => setPreOrderShipmentIds(keys),
            }}
            expandable={{
              rowExpandable: (cs) => (cs.items || []).length > 0,
              expandedRowRender: (cs) => (
                <Table
                  size="small"
                  pagination={false}
                  showHeader={false}
                  rowKey={(item, index) => `${cs.id}-${item.productId || index}`}
                  dataSource={cs.items || []}
                  columns={[
                    {
                      key: "product",
                      render: (_, item) => (
                        <span>
                          {item.productName}
                          {item.productColor ? (
                            <span style={{ color: "rgba(0, 0, 0, 0.45)" }}> ({item.productColor})</span>
                          ) : null}
                        </span>
                      ),
                    },
                    {
                      key: "code",
                      width: 120,
                      render: (_, item) => <Num muted>{item.productCode || "—"}</Num>,
                    },
                    {
                      key: "quantity",
                      width: 70,
                      align: "right",
                      render: (_, item) => <Num>{item.quantity ?? "—"}</Num>,
                    },
                  ]}
                />
              ),
            }}
            columns={[
              {
                title: "Zending",
                dataIndex: "shipmentNumber",
                render: (value) => <Num>{value}</Num>,
              },
              {
                title: t("supplier.supplier"),
                dataIndex: "supplierName",
                render: (value) => value || "-",
              },
              {
                title: "Producten",
                key: "itemCount",
                width: 100,
                align: "right",
                render: (_, cs) => <Num>{(cs.items || []).length}</Num>,
              },
            ]}
          />
        </Modal>

    </div>
  );
};

export default PurchaseOrderShipmentDetail;
